import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import '../styles/erprecruitment.css';
import '../styles/RoleDetailView.css';
import { 
    
    ChevronLeft, 
    Building, 
    MapPin, 
    Hash,
    Layers,
    XCircle,
    MinusCircle,
    PauseCircle,
    PlayCircle,
    FileText,
    Trash2,
    Activity,
    Briefcase,
    Calendar,
    User,
    Globe,
    Award,
    DollarSign,
    Users,
    Clock,
    Eye,
    X,
    Download,
    Zap,
    Table
} from 'lucide-react';
import { formatLocation } from '../config/locationConfig';
import ResumeSubmissionModal from './ResumeSubmissionModal';
import EditApplicationModal from './EditApplicationModal';
import SourceCandidateModal from './SourceCandidateModal';
import ResumeAnalyzer from '../../ATS/components/ResumeAnalyzer';
import PlacementChainDisplay from './PlacementChainDisplay';
import AddCompanyModal from '../../crm/components/AddCompanyModal';

const RoleDetailView = ({ roleId: propRoleId, isModal = false, onClose } = {}) => {
    const { roleId: paramRoleId } = useParams();
    const roleId = propRoleId || paramRoleId;
    const navigate = useNavigate();
    const location = useLocation();
    const isSubmissionPage = location.pathname.startsWith('/submissions');
    const [role, setRole] = useState(null);
    const [applications, setApplications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hiringSteps, setHiringSteps] = useState([]);
    const [applicationResumes, setApplicationResumes] = useState({});
    const [applicationNotes, setApplicationNotes] = useState({});
    const [candidateTypedNotes, setCandidateTypedNotes] = useState({});
    const [savingNoteAppId, setSavingNoteAppId] = useState(null);
    const [expandedCandidateId, setExpandedCandidateId] = useState(null);
    const [selectedInlineResumeId, setSelectedInlineResumeId] = useState(null);
    const [inlinePreviewUrl, setInlinePreviewUrl] = useState('');
    const [inlinePreviewHtml, setInlinePreviewHtml] = useState('');
    const [showHiringProcess, setShowHiringProcess] = useState({});
    const [resumeLoading, setResumeLoading] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [currentResumeUrl, setCurrentResumeUrl] = useState('');
    const [currentCandidateName, setCurrentCandidateName] = useState('');
    const [currentResumeHtml, setCurrentResumeHtml] = useState('');
    const [currentFileType, setCurrentFileType] = useState('');
    const [currentDownloadUrl, setCurrentDownloadUrl] = useState('');
    const [userRole, setUserRole] = useState(localStorage.getItem('role'));
    const [crmCompanies, setCrmCompanies] = useState([]);

    const [showResumeSubmissionModal, setShowResumeSubmissionModal] = useState(false);
    const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);
    const [showSourceCandidateModal, setShowSourceCandidateModal] = useState(false);
    const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
    const [quickAddCompanyData, setQuickAddCompanyData] = useState(null);
    const [applicationToEdit, setApplicationToEdit] = useState(null);
    const [editingChainAppId, setEditingChainAppId] = useState(null);

    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [activeView, setActiveView] = useState('details');
    const jobDescEditorRef = useRef(null);

    const handleEditClick = () => {
        setEditFormData({
            ...role,
            startDate: role.startDate ? new Date(role.startDate).toISOString().split('T')[0] : '',
            endDate: role.endDate ? new Date(role.endDate).toISOString().split('T')[0] : ''
        });
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditFormData({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveChanges = async () => {
        try {
            if (editFormData.client && editFormData.client.trim()) {
                const enteredClient = editFormData.client.trim().toLowerCase();
                const getBase = (str) => (str || '').replace(/\s*\((us|in|uk|ca|au|global|india)\)\s*/gi, '').trim().toLowerCase();
                const enteredBase = getBase(enteredClient);

                // 1. FIRST check for exact match
                let matchedCompany = crmCompanies.find(c => {
                    const disp = (c.DisplayName || '').trim().toLowerCase();
                    const comp = (c.CompanyName || '').trim().toLowerCase();
                    const code = (c.CompanyCode || '').trim().toLowerCase();
                    return disp === enteredClient || comp === enteredClient || code === enteredClient;
                });

                // 2. Only if NO exact match exists, try prefix or base match
                if (!matchedCompany) {
                    matchedCompany = crmCompanies.find(c => {
                        const disp = (c.DisplayName || '').trim().toLowerCase();
                        const comp = (c.CompanyName || '').trim().toLowerCase();
                        const dispBase = getBase(disp);
                        const compBase = getBase(comp);

                        return (enteredBase && (dispBase === enteredBase || compBase === enteredBase)) ||
                               (enteredClient.length >= 2 && (disp.startsWith(enteredClient) || comp.startsWith(enteredClient) || dispBase.startsWith(enteredBase) || compBase.startsWith(enteredBase)));
                    });
                }

                if (!matchedCompany && crmCompanies.length > 0) {
                    Swal.fire({
                        title: 'Invalid Client Selection',
                        text: `"${editFormData.client.trim()}" is not a recognized CRM client. Please select a valid Client from the dropdown list.`,
                        icon: 'warning'
                    });
                    return;
                }
                if (matchedCompany) {
                    editFormData.client = matchedCompany.DisplayName || matchedCompany.CompanyName;
                }
            }

            if (editFormData.status === 'Bench') {
                editFormData.isBench = true;
            } else if (editFormData.status && editFormData.status !== 'Bench') {
                editFormData.isBench = false;
            }

            const token = localStorage.getItem('token');
            await axios.put(`${BASE_URL}/api/recruitment/roles/${roleId}`, editFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire({ title: 'Success', text: 'Role updated successfully', icon: 'success', timer: 1500, showConfirmButton: false });
            setIsEditing(false);
            fetchRoleData();
        } catch (error) {
            console.error('Error updating role:', error);
            Swal.fire({ title: 'Error', text: 'Failed to update role', icon: 'error' });
        }
    };

    const formatCurrency = (amount, currencyCode) => {
        if (amount === undefined || amount === null) return 'N/A';
        const symbols = { 'INR': '₹', 'USD': '$', 'CAD': '$', 'EUR': '€', 'GBP': '£' };
        return `${symbols[currencyCode] || currencyCode}${amount.toLocaleString()}`;
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString();
    };

    const formatDateOnly = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString();
    };

    const formatRoleOwner = (assignTo) => {
        if (!assignTo) return 'N/A';
        if (assignTo.includes('@')) {
            // Extract readable name from email: rakesh.singh@company.com → Rakesh Singh
            const namePart = assignTo.split('@')[0];
            return namePart.split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
        }
        return assignTo;
    };

    const getAssignedRecruiters = (recruiters) => {
        if (!recruiters) return 'N/A';
        try {
            const arr = typeof recruiters === 'string' ? JSON.parse(recruiters) : recruiters;
            if (Array.isArray(arr) && arr.length > 0) {
                return arr.map(r => {
                    if (typeof r === 'string') return r;
                    // Extract name from object — try common name fields
                    return r.name || r.recruiterName || r.label ||
                           (r.email ? r.email.split('@')[0] : null) || 'Unknown';
                }).join(', ');
            }
        } catch {}
        return 'N/A';
    };

    const fetchApplicationResumes = useCallback(async (applicationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASE_URL}/api/recruitment/applications/${applicationId}/resumes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setApplicationResumes(prev => ({ ...prev, [applicationId]: response.data }));
        } catch (error) {
            console.error(`Error fetching resumes:`, error);
        }
    }, []);

    const fetchApplicationStepNotes = useCallback(async (applicationId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASE_URL}/api/recruitment/applications/${applicationId}/step-notes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const notesMap = {};
            const notesData = Array.isArray(response.data) ? response.data : [];
            notesData.forEach(note => { notesMap[note.stepIndex] = note.notes; });
            setApplicationNotes(prev => ({ ...prev, [applicationId]: notesMap }));
        } catch (error) {
            console.error(`Error fetching notes:`, error);
        }
    }, []);

    const fetchApplications = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASE_URL}/api/recruitment/applications/role/${roleId}/with-resumes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setApplications(response.data);
            
            for (const app of response.data) {
                if (app.resumeCount > 0) fetchApplicationResumes(app.id);
                fetchApplicationStepNotes(app.id);
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
        }
    }, [roleId, fetchApplicationResumes, fetchApplicationStepNotes]);

    const fetchRoleData = useCallback(async () => {
        if (!roleId || roleId === 'undefined' || roleId === 'null') return;
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASE_URL}/api/recruitment/roles/${roleId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRole(response.data);
            await fetchApplications();
            
            const stepsResponse = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHiringSteps(stepsResponse.data);
        } catch (error) {
            console.error('Error fetching role details:', error);
        } finally {
            setIsLoading(false);
        }
    }, [roleId, fetchApplications]);

    const fetchCrmCompanies = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${BASE_URL}/api/crm/companies`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && res.data.success && Array.isArray(res.data.data)) {
                setCrmCompanies(res.data.data);
            } else if (Array.isArray(res.data)) {
                setCrmCompanies(res.data);
            }
        } catch (err) {
            console.error('Error fetching CRM companies:', err);
        }
    }, []);

    const handleQuickAddClientToCrm = (clientName) => {
        const trimmed = (clientName || '').trim();
        const hasRegion = trimmed.endsWith('(US)') || trimmed.endsWith('(IN)');
        const locStr = `${role?.country || ''} ${role?.location || ''} ${role?.city || ''} ${role?.state || ''} ${role?.roleLocation || ''}`.toLowerCase();
        const isIndia = locStr.includes('india') || locStr.includes(', in') || locStr.endsWith(' in') || locStr.includes('in,') || locStr === 'in' || trimmed.endsWith('(IN)');
        const regionTag = isIndia ? '(IN)' : '(US)';
        const defaultCountryCode = isIndia ? 'IN' : 'US';

        const finalDisplayName = hasRegion ? trimmed : (trimmed ? `${trimmed} ${regionTag}` : `New Client ${regionTag}`);
        const baseName = trimmed.replace(/\s*\((in|us|usa|india)\)/gi, '').trim() || (trimmed || 'New Client');

        setQuickAddCompanyData({
            CompanyName: baseName,
            DisplayName: finalDisplayName,
            IsClient: true,
            IsSupplier: false,
            IsActive: true,
            IsApproved: true,
            Country: defaultCountryCode
        });
        setShowAddCompanyModal(true);
    };

    const handleCompanyCreated = async () => {
        setShowAddCompanyModal(false);
        await fetchCrmCompanies();
        if (quickAddCompanyData?.DisplayName) {
            setEditFormData(prev => ({ ...prev, client: quickAddCompanyData.DisplayName }));
        }
        Swal.fire({
            title: 'Success!',
            text: `"${quickAddCompanyData?.DisplayName || 'Client'}" created and selected!`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
    };

    useEffect(() => {
        fetchRoleData();
        fetchCrmCompanies();
    }, [fetchRoleData, fetchCrmCompanies]);

    // Sync HTML content into contentEditable div when entering edit mode
    // setTimeout(0) defers until after React has painted the div into the DOM
    useEffect(() => {
        if (isEditing) {
            setTimeout(() => {
                if (jobDescEditorRef.current) {
                    jobDescEditorRef.current.innerHTML = editFormData.jobDescription || '';
                }
            }, 0);
        }
    }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

    const openEditApplicationModal = useCallback((app, e) => {
        if (e) e.stopPropagation();
        setApplicationToEdit(app);
        setShowEditApplicationModal(true);
    }, []);

    const closeEditApplicationModal = useCallback(() => {
        setShowEditApplicationModal(false);
        setApplicationToEdit(null);
    }, []);

    const calculateHiringStepCounts = (apps) => {
        const steps = hiringSteps.length > 0 ? hiringSteps : [];
        const counts = {};
        steps.forEach(s => { counts[s.stepName] = 0; });
        apps.forEach(app => {
            const step = steps[app.currentStep];
            if (step) counts[step.stepName]++;
        });
        return counts;
    };

    const toggleCandidateExpand = (appId, e) => {
        e.stopPropagation();
        setExpandedCandidateId(expandedCandidateId === appId ? null : appId);
        setSelectedInlineResumeId(null);
        setInlinePreviewUrl('');
        setInlinePreviewHtml('');
    };

    const toggleHiringProcess = (applicationId, e) => {
        e.stopPropagation();
        setShowHiringProcess(prev => ({ ...prev, [applicationId]: !prev[applicationId] }));
    };

    const handleDownloadResume = async (resumeId, applicationId, e) => {
        e.stopPropagation();
        try {
            setResumeLoading(true);
            const token = localStorage.getItem('token');
            
            const appResumes = applicationResumes[applicationId] || [];
            const selectedResume = appResumes.find(r => r.id === resumeId);
            const fileName = selectedResume ? selectedResume.resumeFileName : 'resume';
            
            const resumeUrl = `${BASE_URL}/api/recruitment/applications/${applicationId}/resume/${resumeId}`;
            const response = await axios.get(resumeUrl, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            if (!response.data || response.data.size === 0) {
              throw new Error('Received an empty resume file');
            }
            
            const fileURL = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = fileURL;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(fileURL);
        } catch (error) {
            console.error('Error downloading resume:', error);
            Swal.fire({
                title: 'Error Downloading',
                text: 'Failed to download the resume file.',
                icon: 'error'
            });
        } finally {
            setResumeLoading(false);
        }
    };

    const handleInlinePreview = async (resumeId, applicationId, e) => {
        e.stopPropagation();
        try {
            setResumeLoading(true);
            const token = localStorage.getItem('token');
            
            const appResumes = applicationResumes[applicationId] || [];
            const selectedResume = appResumes.find(r => r.id === resumeId);
            const fileName = selectedResume ? selectedResume.resumeFileName : '';
            const fileExtension = fileName ? fileName.substring(fileName.lastIndexOf('.')).toLowerCase() : '';
            const isWordDoc = fileExtension === '.docx' || fileExtension === '.doc';
            
            const resumeUrl = `${BASE_URL}/api/recruitment/applications/${applicationId}/resume/${resumeId}`;
            
            if (isWordDoc) {
                const response = await axios.get(`${resumeUrl}?preview=true`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setInlinePreviewHtml(response.data);
                setInlinePreviewUrl('');
            } else {
                const response = await axios.get(resumeUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });

                if (!response.data || response.data.size === 0) {
                  throw new Error('Received an empty resume file');
                }
                
                const fileURL = URL.createObjectURL(response.data);
                setInlinePreviewUrl(fileURL);
                setInlinePreviewHtml('');
            }
            setSelectedInlineResumeId(resumeId);
        } catch (error) {
            console.error('Error previewing resume:', error);
            Swal.fire({
                title: 'Error Rendering PDF',
                text: 'There was a problem preparing the file for preview.',
                icon: 'error'
            });
        } finally {
            setResumeLoading(false);
        }
    };

    const viewResume = async (resumeId, applicationId, e) => {
        e.stopPropagation();
        try {
            setResumeLoading(true);
            const token = localStorage.getItem('token');
            
            // Find selected resume to check file extension
            const appResumes = applicationResumes[applicationId] || [];
            const selectedResume = appResumes.find(r => r.id === resumeId);
            const fileName = selectedResume ? selectedResume.resumeFileName : '';
            const fileExtension = fileName ? fileName.substring(fileName.lastIndexOf('.')).toLowerCase() : '';
            const isWordDoc = fileExtension === '.docx' || fileExtension === '.doc';

            const app = applications.find(a => a.id === applicationId);
            setCurrentCandidateName(fileName || (app ? app.name : 'Candidate'));
            
            const resumeUrl = `${BASE_URL}/api/recruitment/applications/${applicationId}/resume/${resumeId}`;
            setCurrentDownloadUrl(resumeUrl);
            
            if (isWordDoc) {
                setCurrentFileType(fileExtension.replace('.', ''));
                const response = await axios.get(`${resumeUrl}?preview=true`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCurrentResumeHtml(response.data);
                setCurrentResumeUrl('');
            } else {
                setCurrentFileType('pdf');
                const response = await axios.get(resumeUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });

                if (!response.data || response.data.size === 0) {
                  throw new Error('Received an empty resume file');
                }
                
                const fileURL = URL.createObjectURL(response.data);
                setCurrentResumeUrl(fileURL);
                setCurrentResumeHtml('');
            }
            setShowResumeModal(true);
        } catch (error) {
            console.error('Error viewing resume:', error);
            Swal.fire({
                title: 'Error Rendering PDF',
                text: 'The resume file appears to be corrupted or could not be retrieved from the database.',
                icon: 'error'
            });
        } finally {
            setResumeLoading(false);
        }
    };

    const getAppDisplaySteps = (app) => {
        if (!hiringSteps || hiringSteps.length === 0) return [];
        return hiringSteps.filter(s => {
            const code = (s.stageCode || '').toUpperCase();
            const name = (s.stepName || '').toLowerCase().trim();

            // Always hide all terminal/outcome exit stages from linear progressive track
            if (['REJECTED', 'WITHDRAWN', 'REJECTED_INTERNAL', 'WITHDRAWN_INTERNAL', 'HOLD', 'ON_HOLD', 'HOLD_INTERNAL'].includes(code)) return false;
            if (name === 'rejected' || name === 'withdrawn' || name === 'rejected internally' || name.includes('rejected internal') || name.includes('withdrawn internal') || name === 'hold' || name === 'on hold') return false;

            return true;
        });
    };

    const advanceStep = async (applicationId, app, currentDisplayIdx, appSteps, e) => {
        if (e) e.stopPropagation();
        
        // Find next progressive milestone step (skip exit states on standard forward advancement)
        let targetStep = null;
        for (let i = currentDisplayIdx + 1; i < appSteps.length; i++) {
            const s = appSteps[i];
            if (s.stageCode !== 'REJECTED_INTERNAL' && s.stageCode !== 'WITHDRAWN_INTERNAL' && s.stageCode !== 'HOLD' && s.stageCode !== 'HOLD_INTERNAL') {
                targetStep = s;
                break;
            }
        }
        if (!targetStep) return;

        const targetStepName = targetStep.stepName || '';
        const isOnboarding = targetStepName.toLowerCase().includes('onboarding');

        const result = await Swal.fire({
            title: `Advance to ${targetStepName}?`,
            html: `
                <div style="text-align: left; font-family: inherit;">
                    <p style="font-size: 14px; color: #475569; margin-bottom: 14px;">
                        Move this candidate forward to <strong>${targetStepName}</strong>?
                    </p>
                    ${isOnboarding ? `
                    <div style="margin-bottom: 14px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0;">
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; display: block;">
                            Select Onboarding Entity / Company *
                        </label>
                        <select id="swal-advance-entity-select" style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13.5px; font-weight: 500; color: #0f172a; background: #ffffff; outline: none;">
                            <option value="4" selected>Prophecy Consulting INC (US Entity)</option>
                            <option value="5">Prophecy Offshore (India Entity)</option>
                            <option value="6">Cognifyar Technologies</option>
                        </select>
                    </div>` : ''}
                    <div style="margin-top: 10px;">
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 6px;">
                            Stage Notes / Remarks (Optional)
                        </label>
                        <textarea 
                            id="swal-advance-notes" 
                            placeholder="Add interview feedback, evaluation remarks, or notes for this stage..." 
                            rows="2" 
                            style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical;"
                        ></textarea>
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3b5bfd',
            confirmButtonText: 'Yes, advance',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const entEl = document.getElementById('swal-advance-entity-select');
                const notesEl = document.getElementById('swal-advance-notes');
                return {
                    entityId: entEl ? parseInt(entEl.value, 10) || 4 : 4,
                    notes: notesEl ? notesEl.value.trim() : ''
                };
            }
        });
        if (!result.isConfirmed || !result.value) return;

        const selectedEntityId = result.value.entityId || 4;
        const stepNotes = result.value.notes || '';

        try {
            const token = localStorage.getItem('token');
            let status = 'In Progress';
            if (targetStep.stageCode === 'OFFER') status = 'Offer Extended';
            else if (targetStep.stageCode === 'HIRED') status = 'Hired';

            const rawIndex = hiringSteps.findIndex(s => s.stageCode === targetStep.stageCode || s.stepName === targetStep.stepName);

            await axios.put(`${BASE_URL}/api/recruitment/applications/${applicationId}/status`, 
                { 
                    currentStep: rawIndex >= 0 ? rawIndex : 0, 
                    status, 
                    stepName: targetStepName, 
                    stageCode: targetStep.stageCode,
                    notes: stepNotes,
                    entityId: selectedEntityId 
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire({ title: 'Success!', icon: 'success', timer: 1500, showConfirmButton: false });
            fetchApplications();
        } catch (error) {
            Swal.fire('Error', 'Failed to advance step.', 'error');
        }
    };

    const skipToStep = async (applicationId, targetStep, e) => {
        if (e) e.stopPropagation();
        const targetStepName = targetStep?.stepName || '';
        const targetCode = (targetStep?.stageCode || '').toUpperCase();
        const isOnboarding = targetStepName.toLowerCase().includes('onboarding') || targetCode.includes('ONBOARDING');
        
        const candidate = applications.find(a => a.id === applicationId) || { name: 'Candidate' };
        const candName = candidate?.name || 'Candidate';

        const isCurrentlyOnHold = (candidate?.status || '').toLowerCase() === 'hold' || 
            (candidate?.status || '').toLowerCase() === 'on_hold' || 
            (candidate?.status || '').toLowerCase() === 'on hold' || 
            (candidate?.currentStage || '').toUpperCase() === 'HOLD';

        let chainVisualHtml = '';
        if (isOnboarding) {
            let chainData = [];
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${BASE_URL}/api/crm/chains/submission/${applicationId}?jobId=${role?.id || ''}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data?.success && Array.isArray(res.data.data)) {
                    chainData = res.data.data;
                }
            } catch (err) {
                console.warn('Could not fetch candidate chain preview:', err);
            }

            const clientNodes = chainData.filter(x => (x.ChainSide || '').toUpperCase() === 'CLIENT')
                .sort((a, b) => (a.SequenceNo || 1) - (b.SequenceNo || 1));
            const supplierNodes = chainData.filter(x => (x.ChainSide || '').toUpperCase() === 'SUPPLIER')
                .sort((a, b) => (a.SequenceNo || 1) - (b.SequenceNo || 1));

            const formatNode = (name, badge, colorBg, colorText) => `
                <div style="display: inline-flex; flex-direction: column; align-items: center; gap: 2px;">
                    <div style="background: ${colorBg}; color: ${colorText}; font-weight: 700; font-size: 11.5px; padding: 4px 10px; border-radius: 12px; border: 1px solid rgba(0,0,0,0.08); white-space: nowrap;">
                        ${name}
                    </div>
                    <span style="font-size: 9px; font-weight: 600; color: #64748b;">${badge || ''}</span>
                </div>
            `;

            const nodesList = [];

            if (clientNodes.length > 0) {
                clientNodes.forEach(c => {
                    const cName = c.DisplayName || c.CompanyName || 'Client';
                    const cRole = c.IsEndClient ? 'End Client' : (c.RoleLabel || 'Intermediary Client');
                    nodesList.push(formatNode(cName, cRole, '#f1f5f9', '#1e293b'));
                });
            } else if (role?.client) {
                nodesList.push(formatNode(role.client, 'Client', '#f1f5f9', '#1e293b'));
            } else {
                nodesList.push(formatNode('Client', 'Client', '#f1f5f9', '#1e293b'));
            }

            // Prophecy node
            nodesList.push(formatNode('PROPHECY', 'You', '#3b5bfd', '#ffffff'));

            if (supplierNodes.length > 0) {
                supplierNodes.forEach(s => {
                    const sName = s.DisplayName || s.CompanyName || 'Vendor';
                    nodesList.push(formatNode(sName, s.RoleLabel || 'Supplier', '#fef3c7', '#92400e'));
                });
            } else if (role?.vendorName) {
                nodesList.push(formatNode(role.vendorName, 'Vendor', '#fef3c7', '#92400e'));
            }

            // Candidate node
            nodesList.push(`
                <div style="display: inline-flex; flex-direction: column; align-items: center; gap: 2px;">
                    <div style="width: 26px; height: 26px; border-radius: 50%; background: #ede9fe; color: #7c3aed; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">
                        👤
                    </div>
                    <span style="font-size: 10px; font-weight: 600; color: #475569; max-width: 90px; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        ${candName}
                    </span>
                </div>
            `);

            const arrowHtml = `<span style="color: #94a3b8; font-weight: bold; font-size: 14px; margin: 0 4px; padding-bottom: 12px;">◀</span>`;
            chainVisualHtml = nodesList.join(arrowHtml);
        }

        const result = await Swal.fire({
            title: isOnboarding ? 'Check Placement Pipeline' : isCurrentlyOnHold ? `Unhold & Move to ${targetStepName}?` : `Move to ${targetStepName}?`,
            html: `
                <div style="text-align: left; font-family: inherit;">
                    ${isOnboarding ? `
                    <!-- Placement Pipeline Verification Card -->
                    <div style="background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 10px; padding: 12px 14px; margin-bottom: 12px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                            <span style="font-size: 10.5px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;">
                                Placement Pipeline / Chain
                            </span>
                            <span style="font-size: 12px; font-weight: 700; color: #3b5bfd;">
                                ${candName}
                            </span>
                        </div>
                        <div style="display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 6px; padding: 10px 8px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px;">
                            ${chainVisualHtml}
                        </div>
                    </div>

                    <!-- Verification Warning Box -->
                    <div style="background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 10px 12px; margin-bottom: 12px;">
                        <div style="display: flex; align-items: flex-start; gap: 8px;">
                            <span style="font-size: 16px; line-height: 1;">⚠️</span>
                            <div>
                                <div style="font-weight: 700; font-size: 13px; color: #92400e;">
                                    Please check the pipeline: Is it correct?
                                </div>
                                <div style="font-size: 11.5px; color: #b45309; margin-top: 2px; line-height: 1.4;">
                                    Verify that the client, intermediary, and supplier chain above are accurate. If changes are needed, click <strong>"Edit Pipeline / Chain"</strong> to update it before initiating onboarding.
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Select Entity -->
                    <div style="margin-bottom: 12px; background: #f8fafc; padding: 10px 12px; border-radius: 8px; border: 1px solid #e2e8f0;">
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; display: block;">
                            Select Onboarding Entity / Company *
                        </label>
                        <select id="swal-skip-entity-select" style="width: 100%; padding: 7px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-weight: 500; color: #0f172a; background: #ffffff; outline: none;">
                            <option value="4" selected>Prophecy Consulting INC (US Entity)</option>
                            <option value="5">Prophecy Offshore (India Entity)</option>
                            <option value="6">Cognifyar Technologies</option>
                        </select>
                    </div>
                    ` : isCurrentlyOnHold ? `
                    <div style="background: #f5f3ff; border: 1px solid #c4b5fd; border-radius: 8px; padding: 10px 12px; margin-bottom: 14px;">
                        <div style="font-weight: 700; font-size: 13px; color: #6d28d9; margin-bottom: 4px;">
                            ▶ Unhold & Advance Candidate
                        </div>
                        <div style="font-size: 12.5px; color: #5b21b6; line-height: 1.4;">
                            <strong>${candName}</strong> is currently on hold. Advancing will remove the Hold state, restore active status, and move directly to <strong>${targetStepName}</strong>.
                        </div>
                    </div>
                    ` : `
                    <p style="font-size: 14px; color: #475569; margin-bottom: 14px;">
                        Move this candidate directly to <strong>${targetStepName}</strong>?
                    </p>
                    `}

                    <div>
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 6px;">
                            Stage Notes / Remarks (Optional)
                        </label>
                        <textarea 
                            id="swal-skip-notes" 
                            placeholder="${isOnboarding ? 'Add onboarding notes, rate confirmation, or remarks...' : isCurrentlyOnHold ? 'Add notes regarding un-holding and moving forward...' : 'Add interview feedback, evaluation remarks, or notes for this stage...'}" 
                            rows="2" 
                            style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical;"
                        ></textarea>
                    </div>
                </div>
            `,
            icon: isOnboarding ? undefined : (targetCode.includes('REJECT') ? 'warning' : 'question'),
            showCancelButton: true,
            showDenyButton: isOnboarding,
            confirmButtonColor: targetCode.includes('REJECT') ? '#ef4444' : isCurrentlyOnHold ? '#3b5bfd' : '#3b5bfd',
            denyButtonColor: '#0284c7',
            cancelButtonColor: '#64748b',
            confirmButtonText: isOnboarding ? '✓ Yes, Pipeline is Correct' : isCurrentlyOnHold ? `▶ Unhold & Move to ${targetStepName}` : 'Yes, proceed',
            denyButtonText: '✏️ Edit Pipeline / Chain',
            cancelButtonText: 'Cancel',
            width: isOnboarding ? '560px' : '460px',
            preConfirm: () => {
                const entEl = document.getElementById('swal-skip-entity-select');
                const notesEl = document.getElementById('swal-skip-notes');
                return {
                    entityId: entEl ? parseInt(entEl.value, 10) || 4 : 4,
                    notes: notesEl ? notesEl.value.trim() : ''
                };
            }
        });

        if (result.isDenied) {
            // User requested to edit the pipeline / chain first
            setEditingChainAppId(applicationId);
            setTimeout(() => {
                const chainEl = document.getElementById(`placement-chain-app-${applicationId}`);
                if (chainEl) {
                    chainEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    chainEl.style.boxShadow = '0 0 0 3px rgba(2, 132, 199, 0.4)';
                    setTimeout(() => { chainEl.style.boxShadow = ''; }, 3000);
                }
            }, 100);
            Swal.fire({
                title: 'Edit Candidate Placement Chain',
                text: 'You can now update the placement pipeline above. When finished, click Save and then click Onboarding Initiated to proceed.',
                icon: 'info',
                timer: 4000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
            return;
        }

        if (!result.isConfirmed || !result.value) return;

        const selectedEntityId = result.value.entityId || 4;
        const stepNotes = result.value.notes || '';

        try {
            const token = localStorage.getItem('token');
            let status = 'In Progress';
            if (targetCode === 'REJECTED_INTERNAL' || targetCode === 'REJECTED') status = 'Rejected';
            else if (targetCode === 'WITHDRAWN_INTERNAL' || targetCode === 'WITHDRAWN') status = 'Withdrawn';
            else if (targetCode === 'OFFER') status = 'Offer Extended';
            else if (targetCode === 'HIRED') status = 'Hired';

            const rawIndex = hiringSteps.findIndex(s => s.stageCode === targetStep.stageCode || s.stepName === targetStep.stepName);

            await axios.put(`${BASE_URL}/api/recruitment/applications/${applicationId}/status`, 
                { 
                    currentStep: rawIndex >= 0 ? rawIndex : 0, 
                    status, 
                    stepName: targetStepName, 
                    stageCode: targetStep.stageCode,
                    fromStageCode: isCurrentlyOnHold ? 'HOLD' : undefined,
                    notes: stepNotes || (isCurrentlyOnHold ? `Resumed from Hold and moved directly to ${targetStepName}` : undefined),
                    rejectedAtStage: (status === 'Rejected') ? targetStepName : null,
                    entityId: selectedEntityId 
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire({ 
                title: isCurrentlyOnHold ? 'Candidate Resumed!' : 'Success!', 
                text: isCurrentlyOnHold ? `${candName} unheld and moved to ${targetStepName}.` : undefined,
                icon: 'success', 
                timer: 1800, 
                showConfirmButton: false 
            });
            fetchApplications();
        } catch (error) {
            Swal.fire('Error', 'Failed to update step.', 'error');
        }
    };

    const handleUnholdCandidate = async (applicationId, app, targetStep = null, e) => {
        if (e) e.stopPropagation();
        const appSteps = getAppDisplaySteps(app);
        let resumeStep = targetStep;
        if (!resumeStep && app.rejectedAtStage) {
            resumeStep = appSteps.find(s => s.stepName && s.stepName.toLowerCase().trim() === app.rejectedAtStage.toLowerCase().trim());
        }
        if (!resumeStep && app.currentStep !== undefined && app.currentStep !== null) {
            resumeStep = appSteps[app.currentStep] || hiringSteps[app.currentStep];
        }
        if (!resumeStep) {
            resumeStep = appSteps[0];
        }

        const resumeStageName = resumeStep?.stepName || app.rejectedAtStage || 'Current Stage';
        const resumeStageCode = resumeStep?.stageCode || 'RESUME_REVIEW';
        const rawIndex = hiringSteps.findIndex(s => s.stageCode === resumeStageCode || s.stepName === resumeStageName);

        const result = await Swal.fire({
            title: `Unhold & Resume Candidate?`,
            html: `
                <div style="text-align: left; font-family: inherit;">
                    <p style="font-size: 14px; color: #334155; margin-bottom: 12px; line-height: 1.5;">
                        Resume <strong>${app.name}</strong> as an active candidate at <strong>${resumeStageName}</strong>?
                    </p>
                    <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 10px 12px; margin-bottom: 14px;">
                        <div style="font-size: 12px; color: #1d4ed8; font-weight: 600;">
                            ✓ This will remove the Hold pause and restore active progression in the pipeline.
                        </div>
                    </div>
                    <div>
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 6px;">
                            Resume Remarks / Notes (Optional)
                        </label>
                        <textarea 
                            id="swal-unhold-notes" 
                            placeholder="Add reason for un-holding (e.g., Rate negotiated, candidate ready to proceed)..." 
                            rows="2" 
                            style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical;"
                        ></textarea>
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3b5bfd',
            confirmButtonText: '▶ Yes, Unhold & Resume',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const notesEl = document.getElementById('swal-unhold-notes');
                return {
                    notes: notesEl ? notesEl.value.trim() : ''
                };
            }
        });

        if (!result.isConfirmed) return;

        const unholdNotes = result.value?.notes || '';
        const combinedNotes = `Resumed from Hold at ${resumeStageName}${unholdNotes ? `. Reason: ${unholdNotes}` : ''}`;

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${BASE_URL}/api/recruitment/applications/${applicationId}/status`, {
                status: 'In Progress',
                currentStep: rawIndex >= 0 ? rawIndex : (app.currentStep || 0),
                stepName: resumeStageName,
                stageCode: resumeStageCode,
                fromStageCode: 'HOLD',
                notes: combinedNotes
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                title: 'Candidate Resumed!',
                text: `${app.name} is now active at ${resumeStageName}.`,
                icon: 'success',
                timer: 1800,
                showConfirmButton: false
            });
            fetchApplications();
        } catch (error) {
            console.error('Error unholding candidate:', error);
            Swal.fire('Error', 'Failed to unhold candidate.', 'error');
        }
    };

    const openCandidateOutcomeModal = async (applicationId, app, targetStepIndex, targetStepOrSteps, initialOutcomeType = 'Rejected', e) => {
        if (e) e.stopPropagation();
        
        let targetStep = targetStepOrSteps;
        if (Array.isArray(targetStepOrSteps)) {
            targetStep = targetStepOrSteps[targetStepIndex];
        }
        const currentStageName = targetStep?.stepName || app.currentStage || 'Current Stage';
        const normInitial = (initialOutcomeType || 'Rejected').toLowerCase();
        const isInitialReject = normInitial === 'rejected';
        const isInitialHold = normInitial === 'hold' || normInitial === 'on_hold' || normInitial === 'on hold';
        const isInitialWithdraw = !isInitialReject && !isInitialHold;

        const result = await Swal.fire({
            title: `${isInitialReject ? 'Reject' : isInitialHold ? 'Place on Hold' : 'Withdraw'} Candidate at ${currentStageName}?`,
            html: `
                <div style="text-align: left; font-family: inherit;">
                    <p style="font-size: 13.5px; color: #334155; margin-bottom: 14px;">
                        Select action for <strong>${app.name}</strong> at <strong>${currentStageName}</strong>:
                    </p>
                    
                    <div style="margin-bottom: 14px;">
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 6px;">
                            Action Type *
                        </label>
                        <div style="display: flex; gap: 8px;">
                            <label style="flex: 1; display: flex; align-items: center; gap: 6px; padding: 10px 8px; border: 2px solid #ef4444; border-radius: 8px; cursor: pointer; background: #fef2f2;">
                                <input type="radio" name="outcome_type" value="Rejected" ${isInitialReject ? 'checked' : ''} style="accent-color: #ef4444; width: 15px; height: 15px; cursor: pointer;" />
                                <span style="font-weight: 700; color: #dc2626; font-size: 12.5px;">🔴 Reject</span>
                            </label>
                            <label style="flex: 1; display: flex; align-items: center; gap: 6px; padding: 10px 8px; border: 2px solid #7c3aed; border-radius: 8px; cursor: pointer; background: #f5f3ff;">
                                <input type="radio" name="outcome_type" value="Hold" ${isInitialHold ? 'checked' : ''} style="accent-color: #7c3aed; width: 15px; height: 15px; cursor: pointer;" />
                                <span style="font-weight: 700; color: #7c3aed; font-size: 12.5px;">⏸ Hold</span>
                            </label>
                            <label style="flex: 1; display: flex; align-items: center; gap: 6px; padding: 10px 8px; border: 2px solid #f59e0b; border-radius: 8px; cursor: pointer; background: #fffbeb;">
                                <input type="radio" name="outcome_type" value="Withdrawn" ${isInitialWithdraw ? 'checked' : ''} style="accent-color: #f59e0b; width: 15px; height: 15px; cursor: pointer;" />
                                <span style="font-weight: 700; color: #d97706; font-size: 12.5px;">🟠 Withdraw</span>
                            </label>
                        </div>
                    </div>

                    <div style="margin-bottom: 8px;">
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 6px;">
                            Reason / Remarks <span style="color: #ef4444;">*</span>
                        </label>
                        <textarea 
                            id="swal-outcome-reason" 
                            placeholder="Type the reason for rejection / hold / withdrawal (e.g. Rate discussion on hold, Candidate not interested, etc.)..." 
                            rows="3" 
                            style="width: 100%; padding: 10px 12px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical; line-height: 1.45;"
                        ></textarea>
                    </div>
                </div>
            `,
            icon: isInitialReject ? 'warning' : isInitialHold ? 'info' : 'question',
            showCancelButton: true,
            confirmButtonColor: isInitialReject ? '#ef4444' : isInitialHold ? '#7c3aed' : '#d97706',
            confirmButtonText: isInitialReject ? 'Confirm Rejection' : isInitialHold ? 'Confirm Hold' : 'Confirm Withdrawal',
            cancelButtonText: 'Cancel',
            didOpen: () => {
                const radios = document.querySelectorAll('input[name="outcome_type"]');
                const confirmBtn = Swal.getConfirmButton();
                radios.forEach(r => {
                    r.addEventListener('change', (ev) => {
                        const val = ev.target.value;
                        if (val === 'Rejected') {
                            if (confirmBtn) {
                                confirmBtn.style.backgroundColor = '#ef4444';
                                confirmBtn.innerText = 'Confirm Rejection';
                            }
                        } else if (val === 'Hold') {
                            if (confirmBtn) {
                                confirmBtn.style.backgroundColor = '#7c3aed';
                                confirmBtn.innerText = 'Confirm Hold';
                            }
                        } else {
                            if (confirmBtn) {
                                confirmBtn.style.backgroundColor = '#d97706';
                                confirmBtn.innerText = 'Confirm Withdrawal';
                            }
                        }
                    });
                });
            },
            preConfirm: () => {
                const outcomeType = document.querySelector('input[name="outcome_type"]:checked')?.value || 'Rejected';
                const reason = document.getElementById('swal-outcome-reason')?.value?.trim() || '';
                if (!reason) {
                    Swal.showValidationMessage(`Please enter the reason for ${outcomeType.toLowerCase()}`);
                    return false;
                }
                return { outcomeType, reason };
            }
        });

        if (!result.isConfirmed || !result.value) return;

        const { outcomeType, reason } = result.value;
        const isReject = outcomeType === 'Rejected';
        const isHold = outcomeType === 'Hold';
        const combinedNotes = `${outcomeType} at ${currentStageName}. Reason: ${reason}`;

        try {
            const token = localStorage.getItem('token');
            const targetStageCode = isReject ? 'REJECTED' : isHold ? 'HOLD' : 'WITHDRAWN';
            await axios.put(`${BASE_URL}/api/recruitment/applications/${applicationId}/status`, {
                status: outcomeType,
                currentStep: targetStepIndex,
                stepName: currentStageName,
                stageCode: targetStageCode,
                fromStageCode: targetStep?.stageCode,
                rejectedAtStage: currentStageName,
                notes: combinedNotes
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                title: isReject ? 'Candidate Rejected' : isHold ? 'Candidate Placed On Hold' : 'Candidate Withdrawn',
                text: `${app.name} marked as ${outcomeType} at ${currentStageName}.`,
                icon: isReject ? 'error' : isHold ? 'info' : 'warning',
                timer: 2000,
                showConfirmButton: false
            });
            fetchApplications();
        } catch (error) {
            console.error('Error updating outcome status:', error);
            Swal.fire('Error', 'Failed to update candidate status.', 'error');
        }
    };

    const openCurrentStageNotesModal = async (applicationId, app, currentDisplayIndex, appSteps, e) => {
        if (e) e.stopPropagation();
        const activeStep = appSteps[currentDisplayIndex];
        const currentStageName = activeStep?.stepName || app.currentStage || 'Current Stage';

        // Retrieve existing note, author, and timestamp for this stage
        let existingNote = '';
        let existingAuthor = '';
        let existingDate = '';
        if (app.stageHistory && app.stageHistory.length > 0) {
            const rawIndex = hiringSteps.findIndex(s => s.stageCode === activeStep?.stageCode || s.stepName === activeStep?.stepName);
            const matchingItems = app.stageHistory.filter(h => 
                (rawIndex >= 0 && h.ToStep === rawIndex) ||
                (h.ToStageCode && h.ToStageCode === activeStep?.stageCode) ||
                (h.ToStageCode && h.ToStageCode === app.currentStage)
            );
            const historyItem = matchingItems.length > 0 ? matchingItems[matchingItems.length - 1] : null;
            if (historyItem && historyItem.Notes && historyItem.Notes.trim() && historyItem.Notes.trim() !== '—') {
                existingNote = historyItem.Notes.trim();
                existingAuthor = (historyItem.ChangedByUserName && historyItem.ChangedByUserName.toLowerCase() !== 'admin') ? historyItem.ChangedByUserName : 'Abarna Sankar';
                existingDate = formatDateTime(historyItem.ChangedAtUtc);
            }
        }

        const result = await Swal.fire({
            title: `Stage Notes for ${currentStageName}`,
            html: `
                <div style="text-align: left; font-family: inherit;">
                    <p style="font-size: 13.5px; color: #334155; margin-bottom: 12px;">
                        Candidate: <strong>${app.name}</strong><br />
                        Current Stage: <strong>${currentStageName}</strong>
                    </p>
                    ${existingNote ? `
                    <div style="background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 12px; margin-bottom: 12px;">
                        <div style="font-size: 11px; font-weight: 700; color: #3b5bfd; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; display: flex; justify-content: space-between; align-items: center;">
                            <span>Existing Saved Note</span>
                            <span style="font-size: 10.5px; color: #64748b; text-transform: none;">${existingAuthor ? `${existingAuthor} • ` : ''}${existingDate}</span>
                        </div>
                        <div style="font-size: 13px; color: #0f172a; font-weight: 500; line-height: 1.45;">
                            "${existingNote}"
                        </div>
                    </div>` : ''}
                    <div>
                        <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 6px;">
                            ${existingNote ? 'Update / Edit Note' : 'Add Note for this Stage'}
                        </label>
                        <textarea 
                            id="swal-current-stage-notes" 
                            placeholder="Type notes, interview feedback, or remarks for ${currentStageName}..." 
                            rows="3" 
                            style="width: 100%; padding: 10px 12px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical; line-height: 1.45;"
                        >${existingNote || ''}</textarea>
                    </div>
                </div>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#3b5bfd',
            confirmButtonText: existingNote ? '💾 Update Note' : '💾 Save Note',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const notesEl = document.getElementById('swal-current-stage-notes');
                const noteText = notesEl ? notesEl.value.trim() : '';
                if (!noteText) {
                    Swal.showValidationMessage('Please type a note before saving');
                    return false;
                }
                return { noteText };
            }
        });

        if (!result.isConfirmed || !result.value) return;

        try {
            const token = localStorage.getItem('token');
            const rawIndex = hiringSteps.findIndex(s => s.stageCode === activeStep?.stageCode || s.stepName === activeStep?.stepName);
            await axios.post(`${BASE_URL}/api/recruitment/applications/step-notes`, {
                applicationId,
                stepIndex: rawIndex >= 0 ? rawIndex : (app.currentStep || 0),
                stepName: currentStageName,
                notes: result.value.noteText
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire({ title: 'Saved!', text: 'Stage note added successfully.', icon: 'success', timer: 1500, showConfirmButton: false });
            fetchApplications();
        } catch (error) {
            console.error('Error saving stage note:', error);
            Swal.fire('Error', 'Failed to save stage note.', 'error');
        }
    };

    const handleSaveCandidateNote = async (applicationId, app) => {
        const noteText = (candidateTypedNotes[applicationId] || '').trim();
        if (!noteText) {
            Swal.fire('Please enter a note', 'Note content cannot be empty.', 'info');
            return;
        }
        try {
            setSavingNoteAppId(applicationId);
            const token = localStorage.getItem('token');
            const activeStep = (app.currentStage && hiringSteps.find(s => s.stageCode === app.currentStage)) || hiringSteps[app.currentStep] || hiringSteps[0];
            await axios.post(`${BASE_URL}/api/recruitment/applications/step-notes`, {
                applicationId,
                stepIndex: app.currentStep || 0,
                stepName: activeStep?.stepName || app.currentStage || 'Current Stage',
                notes: noteText
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setCandidateTypedNotes(prev => ({ ...prev, [applicationId]: '' }));
            Swal.fire({ title: 'Saved!', text: 'Note saved successfully.', icon: 'success', timer: 1500, showConfirmButton: false });
            fetchApplications();
        } catch (err) {
            console.error('Error saving note:', err);
            Swal.fire('Error', 'Failed to save note.', 'error');
        } finally {
            setSavingNoteAppId(null);
        }
    };

    const getInitials = (name) => {
        if (!name) return '??';
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
    };

    const deleteApplication = async (applicationId, name, e) => {
        if (e) e.stopPropagation();
        const result = await Swal.fire({
            title: 'Delete?',
            text: `Permanently delete ${name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Yes, delete'
        });
        if (!result.isConfirmed) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${BASE_URL}/api/recruitment/applications/${applicationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire('Deleted!', 'Removed successfully.', 'success');
            fetchApplications();
        } catch (error) {
            Swal.fire('Error', 'Failed to delete.', 'error');
        }
    };

    const generateHiringSteps = (app) => {
        const appSteps = getAppDisplaySteps(app);
        if (!appSteps || appSteps.length === 0) return null;
        const statusLower = (app.status || '').toLowerCase();
        const isRejectedState = statusLower === 'rejected';
        const isWithdrawnState = statusLower === 'withdrawn';
        const isHoldState = statusLower === 'hold' || statusLower === 'on_hold' || statusLower === 'on hold' || (app.currentStage || '').toUpperCase() === 'HOLD';
        const isTerminated = isRejectedState || isWithdrawnState;

        // Find current position in appSteps
        let currentDisplayIndex = -1;
        if ((isTerminated || isHoldState) && app.rejectedAtStage) {
            currentDisplayIndex = appSteps.findIndex(s => 
                s.stepName && s.stepName.toLowerCase().trim() === app.rejectedAtStage.toLowerCase().trim()
            );
        }
        if (currentDisplayIndex === -1 && app.currentStage) {
            currentDisplayIndex = appSteps.findIndex(s => 
                (s.stageCode && s.stageCode.toUpperCase() === app.currentStage.toUpperCase()) ||
                (s.stepName && s.stepName.toLowerCase().replace(/_/g, ' ') === app.currentStage.toLowerCase().replace(/_/g, ' '))
            );
        }
        if (currentDisplayIndex === -1 && app.currentStep !== undefined && app.currentStep !== null) {
            const rawStep = (app.currentStage && hiringSteps.find(s => s.stageCode === app.currentStage)) || hiringSteps[app.currentStep];
            if (rawStep) {
                currentDisplayIndex = appSteps.findIndex(s => 
                    s.stageCode === rawStep.stageCode || 
                    s.stepName.toLowerCase() === rawStep.stepName.toLowerCase()
                );
            }
        }
        if (currentDisplayIndex === -1) {
            currentDisplayIndex = Math.min(app.currentStep || 0, appSteps.length - 1);
        }

        return (
            <div className="rd-pipeline-steps-container">
                {appSteps.map((step, index) => {
                    const isCompleted = index < currentDisplayIndex;
                    const isCurrent = index === currentDisplayIndex;
                    const isFuture = index > currentDisplayIndex;
                    
                    const isRejectedPoint = isRejectedState && isCurrent;
                    const isWithdrawnPoint = isWithdrawnState && isCurrent;
                    const isHoldPoint = isHoldState && isCurrent;
                    const isHired = statusLower === 'hired' && index === appSteps.length - 1;
                    
                    let cls = 'rd-step-pending';
                    let circleContent = index + 1;

                    if (isRejectedPoint) {
                        cls = 'rd-step-rejected';
                        circleContent = '✗';
                    } else if (isWithdrawnPoint) {
                        cls = 'rd-step-withdrawn';
                        circleContent = '🚫';
                    } else if (isHoldPoint) {
                        cls = 'rd-step-hold';
                        circleContent = '⏸';
                    } else if (isHired || isCompleted) {
                        cls = 'rd-step-completed';
                        circleContent = '✓';
                    } else if (isCurrent && !isTerminated && !isHoldState) {
                        cls = 'rd-step-current';
                        circleContent = index + 1;
                    } else if (isFuture && isTerminated) {
                        cls = 'rd-step-terminated-future';
                        circleContent = '✕';
                    } else if (isFuture && isHoldState) {
                        cls = 'rd-step-pending rd-step-hold-future';
                        circleContent = index + 1;
                    }

                    const isCurrentInteractive = isCurrent && !isTerminated && !isHired;
                    const isFutureInteractive = isFuture && !isTerminated && !isHired;
                    const isInteractive = isCurrentInteractive || isFutureInteractive;
                    
                    const isSubmittedToClient = (step.stepName && step.stepName.toLowerCase().includes('submitted to client')) || step.stageCode === 'SUBMITTED_TO_CLIENT';
                    let stepTimestampObj = (step.stageCode && app.stageTimestampsByCode?.[step.stageCode]) || app.stageTimestamps?.[index];
                    if (!stepTimestampObj) {
                        if (isSubmittedToClient && app.appliedAt) {
                            stepTimestampObj = { timestamp: app.appliedAt, changedBy: app.submittedBy || 'Recruiter' };
                        } else if (index === 0 && app.appliedAt) {
                            stepTimestampObj = { timestamp: app.appliedAt, changedBy: app.submittedBy || 'Recruiter' };
                        }
                    }

                    const hasReachedStep = isCompleted || isCurrent || isHired;
                    const stepDateTimeStr = (hasReachedStep && stepTimestampObj?.timestamp) ? formatDateTime(stepTimestampObj.timestamp) : null;
                    const stepTooltip = isTerminated && isFuture
                        ? `${step.stepName} (Not available - process ended)`
                        : isHoldState && isFuture
                            ? `${step.stepName} (Click to unhold and move directly to ${step.stepName})`
                            : isHoldPoint
                                ? `${step.stepName} (On Hold - Click to unhold / resume candidate, or click another stage to move)`
                                : isCurrentInteractive
                                    ? `${step.stepName} (Current Stage - Click to add/update note)`
                                    : isFutureInteractive
                                        ? `${step.stepName} (Click to move to ${step.stepName})`
                                        : stepDateTimeStr
                                            ? `${step.stepName}\nDate & Time: ${stepDateTimeStr}${stepTimestampObj.changedBy ? `\nUpdated by: ${stepTimestampObj.changedBy}` : ''}`
                                            : step.stepName;

                    return (
                        <div key={index} 
                            className={`rd-pipeline-step ${cls} ${isInteractive ? 'rd-step-advanceable' : ''}`}
                            title={stepTooltip}
                            onClick={(e) => {
                                if (isHoldPoint) {
                                    handleUnholdCandidate(app.id, app, step, e);
                                } else if (isCurrentInteractive) {
                                    openCurrentStageNotesModal(app.id, app, currentDisplayIndex, appSteps, e);
                                } else if (isFutureInteractive) {
                                    skipToStep(app.id, step, e);
                                }
                            }}>
                            <div className="rd-step-number">
                                {circleContent}
                            </div>
                            <div className="rd-step-label">
                                {isWithdrawnPoint ? `${step.stepName} Withdrawn` : (isRejectedPoint ? `${step.stepName} Rejected` : (isHoldPoint ? `${step.stepName} (On Hold)` : step.stepName))}
                            </div>
                            {stepDateTimeStr && (
                                <div className="rd-step-timestamp">
                                    {new Date(stepTimestampObj.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                    <br />
                                    {new Date(stepTimestampObj.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            )}
                            {!isTerminated && !isHired && (
                                <div className="rd-step-actions-container" onClick={(e) => e.stopPropagation()}>
                                    <button 
                                        type="button"
                                        className="rd-step-action-btn rd-step-reject-btn" 
                                        title={`Reject Candidate at ${step.stepName}`}
                                        onClick={(e) => openCandidateOutcomeModal(app.id, app, index, step, 'Rejected', e)}
                                    >
                                        <XCircle size={13} />
                                    </button>
                                    {isHoldState ? (
                                        <button 
                                            type="button"
                                            className="rd-step-action-btn rd-step-unhold-btn" 
                                            title={`Unhold / Resume Candidate at ${step.stepName}`}
                                            onClick={(e) => handleUnholdCandidate(app.id, app, step, e)}
                                        >
                                            <PlayCircle size={13} />
                                        </button>
                                    ) : (
                                        <button 
                                            type="button"
                                            className="rd-step-action-btn rd-step-hold-btn" 
                                            title={`Hold Candidate at ${step.stepName}`}
                                            onClick={(e) => openCandidateOutcomeModal(app.id, app, index, step, 'Hold', e)}
                                        >
                                            <PauseCircle size={13} />
                                        </button>
                                    )}
                                    <button 
                                        type="button"
                                        className="rd-step-action-btn rd-step-withdraw-btn" 
                                        title={`Withdraw Candidate at ${step.stepName}`}
                                        onClick={(e) => openCandidateOutcomeModal(app.id, app, index, step, 'Withdrawn', e)}
                                    >
                                        <MinusCircle size={13} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderExpandedDetails = (app) => {
        const submittedDate = formatDateTime(app.appliedAt || app.createdAt || app.created_at || app.submittedAt || app.submitted_at);
        const statusLower = (app.status || '').toLowerCase();
        const isRejectedState = app.rejected || statusLower === 'rejected' || (app.currentStage || '').toUpperCase() === 'REJECTED';
        const isWithdrawnState = app.withdrawn || statusLower === 'withdrawn' || (app.currentStage || '').toUpperCase() === 'WITHDRAWN';
        const isTerminated = isRejectedState || isWithdrawnState;
        const displaySubmittedBy = (app.submittedBy && app.submittedBy.toLowerCase() !== 'admin') ? app.submittedBy : 'Abarna Sankar';

        return (
            <div className="candidate-expanded-content">
                <div className="submission-audit-row">
                    <span><strong>Submitted:</strong> {submittedDate}</span>
                    <span className="submitted-by"><strong>Submitted by:</strong> {displaySubmittedBy}</span>
                </div>

                {/* ── 1. STAGE PROGRESSION HISTORY (TOP) ── */}
                {app.stageHistory && app.stageHistory.length > 0 && (() => {
                    const consolidatedHistory = [];
                    app.stageHistory.forEach(h => {
                        const toCode = (h.ToStageCode || '').toUpperCase();
                        const isHistReject = toCode === 'REJECTED' || toCode === 'REJECTED_INTERNAL';
                        const isHistWithdraw = toCode === 'WITHDRAWN' || toCode === 'WITHDRAWN_INTERNAL';
                        const isHistHold = toCode === 'HOLD' || toCode === 'ON_HOLD' || toCode === 'HOLD_INTERNAL';
                        const isTerminatedRow = isHistReject || isHistWithdraw || isHistHold;
                        const targetStepIndex = h.ToStep;
                        const matchedStep = (h.ToStageCode && hiringSteps.find(s => s.stageCode === h.ToStageCode)) || hiringSteps[targetStepIndex];
                        const targetStageName = matchedStep?.stepName || h.ToStageCode || (targetStepIndex !== undefined && targetStepIndex !== null ? `Step ${targetStepIndex + 1}` : 'Stage');
                        const changedBy = h.ChangedByUserName && h.ChangedByUserName.toLowerCase() !== 'admin' ? h.ChangedByUserName : 'Abarna Sankar';

                        if (isTerminatedRow) {
                            const existingEntry = consolidatedHistory.find(item => 
                                (targetStepIndex !== undefined && targetStepIndex !== null && item.stepIndex === targetStepIndex) || 
                                item.stageName.toLowerCase() === targetStageName.toLowerCase()
                            );

                            if (existingEntry) {
                                existingEntry.outcomeType = isHistReject ? 'REJECTED' : isHistHold ? 'HOLD' : 'WITHDRAWN';
                                existingEntry.outcomeAt = h.ChangedAtUtc;
                                existingEntry.outcomeBy = changedBy;
                                existingEntry.outcomeNotes = h.Notes;
                            } else {
                                consolidatedHistory.push({
                                    stepIndex: targetStepIndex,
                                    stageName: targetStageName,
                                    enteredAt: null,
                                    outcomeType: isHistReject ? 'REJECTED' : isHistHold ? 'HOLD' : 'WITHDRAWN',
                                    outcomeAt: h.ChangedAtUtc,
                                    outcomeBy: changedBy,
                                    outcomeNotes: h.Notes,
                                    enteredBy: changedBy
                                });
                            }
                        } else {
                            const existingEntry = consolidatedHistory.find(item => 
                                (targetStepIndex !== undefined && targetStepIndex !== null && item.stepIndex === targetStepIndex) || 
                                item.stageName.toLowerCase() === targetStageName.toLowerCase()
                            );
                            if (existingEntry) {
                                if (!existingEntry.enteredAt) {
                                    existingEntry.enteredAt = h.ChangedAtUtc;
                                }
                                if (h.Notes && h.Notes.trim() && h.Notes.trim() !== '—') {
                                    existingEntry.notes = h.Notes.trim();
                                    existingEntry.enteredBy = changedBy;
                                }
                            } else {
                                consolidatedHistory.push({
                                    stepIndex: targetStepIndex,
                                    stageName: targetStageName,
                                    enteredAt: h.ChangedAtUtc,
                                    enteredBy: changedBy,
                                    outcomeType: null,
                                    outcomeAt: null,
                                    outcomeBy: null,
                                    notes: h.Notes
                                });
                            }
                        }
                    });

                    return (
                        <div className="detail-grid-section" style={{ background: '#f8fafc', padding: '14px 18px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Clock size={15} color="#3b5bfd" /> Stage Progression History
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: '#ffffff', borderRadius: '6px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', textAlign: 'left', color: '#475569' }}>
                                            <th style={{ padding: '8px 14px', fontWeight: 700, width: '22%' }}>Stage</th>
                                            <th style={{ padding: '8px 14px', fontWeight: 700, width: '24%' }}>Date & Time</th>
                                            <th style={{ padding: '8px 14px', fontWeight: 700, width: '20%' }}>Updated By</th>
                                            <th style={{ padding: '8px 14px 8px 32px', fontWeight: 700, width: '34%' }}>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {consolidatedHistory.map((item, idx) => {
                                            const isReject = item.outcomeType === 'REJECTED';
                                            const isWithdraw = item.outcomeType === 'WITHDRAWN';
                                            const isHold = item.outcomeType === 'HOLD';
                                            const isTerminated = isReject || isWithdraw || isHold;
                                            const isDifferentUsers = isTerminated && item.enteredBy && item.outcomeBy && item.enteredBy.trim().toLowerCase() !== item.outcomeBy.trim().toLowerCase();
                                            let updatedByDisplay = item.outcomeBy || item.enteredBy || 'Abarna Sankar';
                                            if (isDifferentUsers) {
                                                updatedByDisplay = (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px' }}>
                                                        <div style={{ color: '#475569' }}>
                                                            <span style={{ fontWeight: 600, color: '#334155' }}>Entered: </span>{item.enteredBy}
                                                        </div>
                                                        <div style={{ color: isReject ? '#dc2626' : isHold ? '#7c3aed' : '#d97706', fontWeight: 600 }}>
                                                            <span>{isReject ? 'Rejected: ' : isHold ? 'Hold: ' : 'Withdrawn: '}</span>{item.outcomeBy}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            const stageRemarks = item.notes && item.notes.trim() ? item.notes.trim() : null;
                                            const outcomeRemarks = item.outcomeNotes && item.outcomeNotes.trim() ? item.outcomeNotes.trim() : null;
                                            const fullTooltipText = [
                                                stageRemarks ? `Stage Note: ${stageRemarks}` : null,
                                                outcomeRemarks ? `${isReject ? 'Rejection' : isHold ? 'Hold' : 'Withdrawal'} Reason: ${outcomeRemarks}` : null
                                            ].filter(Boolean).join('\n\n');

                                                    return (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                            <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0f172a', verticalAlign: 'top' }}>
                                                                <div style={{ fontSize: '13px', color: '#1e293b' }}>{item.stageName}</div>
                                                                {isTerminated && (
                                                                    <div style={{ 
                                                                        fontSize: '11.5px', 
                                                                        fontWeight: 700, 
                                                                        color: isReject ? '#dc2626' : isHold ? '#7c3aed' : '#d97706', 
                                                                        marginTop: '3px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}>
                                                                        {isReject ? '✗ Rejected' : isHold ? '⏸ On Hold' : '🚫 Withdrawn'}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '10px 14px', color: '#334155', verticalAlign: 'top' }}>
                                                                {item.enteredAt && (
                                                                    <div style={{ fontSize: '12px', color: '#475569' }}>
                                                                        {isTerminated && <span style={{ fontWeight: 600, color: '#334155' }}>Entered: </span>}
                                                                        {formatDateTime(item.enteredAt)}
                                                                    </div>
                                                                )}
                                                                {isTerminated && item.outcomeAt && (
                                                                    <div style={{ 
                                                                        fontSize: '12px', 
                                                                        color: isReject ? '#dc2626' : isHold ? '#7c3aed' : '#d97706', 
                                                                        fontWeight: 600, 
                                                                        marginTop: item.enteredAt ? '3px' : '0' 
                                                                    }}>
                                                                        <span>{isReject ? 'Rejected: ' : isHold ? 'Hold: ' : 'Withdrawn: '}</span>
                                                                        {formatDateTime(item.outcomeAt)}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '10px 14px', color: '#475569', fontWeight: 500, verticalAlign: 'top' }}>
                                                                {updatedByDisplay}
                                                            </td>
                                                            <td 
                                                                title={fullTooltipText || undefined}
                                                                style={{ 
                                                                    padding: '8px 14px', 
                                                                    color: '#334155', 
                                                                    verticalAlign: 'top', 
                                                                    maxWidth: '460px',
                                                                    cursor: fullTooltipText ? 'help' : 'default'
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12.5px', lineHeight: 1.35 }}>
                                                                    {stageRemarks && (
                                                                        <div 
                                                                            title={`Stage Note: ${stageRemarks}`}
                                                                            style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}
                                                                        >
                                                                            <span style={{ 
                                                                                fontSize: '10.5px', 
                                                                                fontWeight: 700, 
                                                                                color: '#3b5bfd', 
                                                                                background: '#ccfbf1', 
                                                                                padding: '1px 6px', 
                                                                                borderRadius: '4px',
                                                                                whiteSpace: 'nowrap',
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.02em',
                                                                                lineHeight: '16px'
                                                                            }}>
                                                                                Stage
                                                                            </span>
                                                                            <span style={{ color: '#1e293b', wordBreak: 'break-word' }}>
                                                                                {stageRemarks}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    {outcomeRemarks && (
                                                                        <div 
                                                                            title={`${isReject ? 'Rejection' : isHold ? 'Hold' : 'Withdrawal'} Reason: ${outcomeRemarks}`}
                                                                            style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}
                                                                        >
                                                                            <span style={{ 
                                                                                fontSize: '10.5px', 
                                                                                fontWeight: 700, 
                                                                                color: isReject ? '#dc2626' : isHold ? '#7c3aed' : '#d97706', 
                                                                                background: isReject ? '#fee2e2' : isHold ? '#ede9fe' : '#fef3c7', 
                                                                                padding: '1px 6px', 
                                                                                borderRadius: '4px',
                                                                                whiteSpace: 'nowrap',
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.02em',
                                                                                lineHeight: '16px'
                                                                            }}>
                                                                                {isReject ? 'Rejected' : isHold ? 'On Hold' : 'Withdrawn'}
                                                                            </span>
                                                                            <span style={{ color: isReject ? '#991b1b' : isHold ? '#5b21b6' : '#92400e', wordBreak: 'break-word', fontWeight: 500 }}>
                                                                                {outcomeRemarks}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    {!stageRemarks && !outcomeRemarks && (
                                                                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })()}



                {/* ── 3. PERSONAL INFORMATION ── */}
                <div className="detail-grid-section">
                    <h4>Personal Information</h4>
                    <div className="expanded-detail-grid">
                        <div className="detail-field">
                            <label>First Name</label>
                            <span>{app.candidateFirstName || (app.name ? app.name.trim().split(/\s+/)[0] : 'Not Provided')}</span>
                        </div>
                        <div className="detail-field">
                            <label>Last Name</label>
                            <span>{app.candidateLastName || (app.name ? (app.name.trim().split(/\s+/).slice(1).join(' ') || 'Not Provided') : 'Not Provided')}</span>
                        </div>
                        <div className="detail-field"><label>Email</label><span>{app.email || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Phone</label><span>{app.phone || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Date of Birth</label><span>{app.dateOfBirth ? new Date(app.dateOfBirth).toLocaleDateString() : 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Current Location</label><span>{app.currentLocation || app.location || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>LinkedIn</label>{app.linkedInUrl ? <a href={app.linkedInUrl} target="_blank" rel="noopener noreferrer">Link</a> : 'Not Provided'}</div>
                        <div className="detail-field"><label>Passport</label><span>{app.passport || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Work Auth</label><span>{app.workAuthorization || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Date Availability</label><span>{app.dateAvailability ? new Date(app.dateAvailability).toLocaleDateString() : 'Not Provided'}</span></div>
                    </div>
                </div>

                {/* ── 4. PROFESSIONAL DETAILS ── */}
                <div className="detail-grid-section">
                    <h4>Professional Details</h4>
                    <div className="expanded-detail-grid">
                        <div className="detail-field"><label>Total IT Experience</label><span>{app.totalITExperience || app.experience || 'Not Provided'} {app.totalITExperience || app.experience ? 'Years' : ''}</span></div>
                        <div className="detail-field"><label>Relevant Experience</label><span>{app.relevantExperience || 'Not Provided'} {app.relevantExperience ? 'Years' : ''}</span></div>
                        <div className="detail-field"><label>Current Company</label><span>{app.currentCompany || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Current Employer</label><span>{app.currentEmployer || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Expected Salary</label><span>{app.expectedSalary || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Rate</label><span>{app.rate || 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Notice Period</label><span>{app.noticePeriod ? `${app.noticePeriod} Days` : 'Not Provided'}</span></div>
                        <div className="detail-field"><label>Highest Degree</label><span>{app.highestDegree || 'Not Provided'}</span></div>
                        <div className="detail-field full-width"><label>Skills</label><span className="skills-tags">{app.skills || 'Not Provided'}</span></div>
                        <div className="detail-field full-width"><label>Current Employer Address</label><span>{app.currentEmployerAddress || 'Not Provided'}</span></div>
                    </div>
                </div>

                {(app.isFormerTCSEmployee || app.isFormerTCSBusinessAssociate) && (
                    <div className="detail-grid-section">
                        <h4>TCS Information</h4>
                        <div className="expanded-detail-grid">
                            <div className="detail-field"><label>Former TCS Employee</label><span>{app.isFormerTCSEmployee ? 'Yes' : 'No'}</span></div>
                            {app.isFormerTCSEmployee && <div className="detail-field"><label>TCS Employee ID</label><span>{app.tcsEmployeeId || 'N/A'}</span></div>}
                            <div className="detail-field"><label>Former TCS Business Associate</label><span>{app.isFormerTCSBusinessAssociate ? 'Yes' : 'No'}</span></div>
                            {app.isFormerTCSBusinessAssociate && <div className="detail-field"><label>TCS Business Associate ID</label><span>{app.tcsBusinessAssociateId || 'N/A'}</span></div>}
                        </div>
                    </div>
                )}

                {/* ── 5. RESUMES & PREVIEW ── */}
                <div className="expanded-actions-row" style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', width: '100%' }}>
                        <div style={{ flex: selectedInlineResumeId ? '0 0 350px' : '1', transition: 'all 0.3s' }}>
                            <h3 style={{ fontSize: 18, marginBottom: 10, color: '#111' }}>
                                <FileText size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                Resume Document{(applicationResumes[app.id]?.length || 0) > 1 ? 's' : ''}
                            </h3>
                            {applicationResumes[app.id]?.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {applicationResumes[app.id].map(res => (
                                        <div key={res.id} className="candidate-document-item" style={{ 
                                            background: '#ffffff',
                                            border: selectedInlineResumeId === res.id ? '2px solid #3b5bfd' : '1px solid #e5e7eb',
                                            padding: '12px',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}>
                                            <FileText size={28} className="candidate-document-icon" color="#3b5bfd" />
                                            <div className="candidate-document-info" style={{ flex: 1 }}>
                                                <div className="candidate-document-name" title={res.resumeFileName} style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>
                                                    {res.resumeFileName}
                                                </div>
                                                <div className="candidate-document-meta" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'flex', gap: '10px' }}>
                                                    {res.resumeFileSizeBytes && <span>Size: {(res.resumeFileSizeBytes / 1024).toFixed(2)} KB</span>}
                                                    {res.resumeFileExtension && <span>Type: {res.resumeFileExtension.toUpperCase().replace('.', '')}</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button 
                                                    onClick={(e) => handleInlinePreview(res.id, app.id, e)}
                                                    style={{ padding: '6px 12px', background: '#3b5bfd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
                                                    disabled={resumeLoading}
                                                >
                                                    <Eye size={14} /> Preview
                                                </button>
                                                <button 
                                                    onClick={(e) => handleDownloadResume(res.id, app.id, e)}
                                                    style={{ padding: '6px 12px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
                                                    disabled={resumeLoading}
                                                >
                                                    <Download size={14} /> Download
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', color: '#9ca3af', border: '1px dashed #d1d5db', borderRadius: '6px' }}>
                                    <FileText size={56} style={{ marginBottom: 14, opacity: 0.35 }} />
                                    <p style={{ margin: 0, fontSize: 15 }}>No resumes uploaded</p>
                                </div>
                            )}
                        </div>

                        {selectedInlineResumeId && (
                            <div style={{ flex: '1', minWidth: '400px', height: '600px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ background: '#3b5bfd', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Eye size={16} color="#fff" />
                                        Preview: {applicationResumes[app.id].find(r => r.id === selectedInlineResumeId)?.resumeFileName}
                                    </span>
                                    <button 
                                        onClick={() => {
                                            setSelectedInlineResumeId(null);
                                            setInlinePreviewUrl('');
                                            setInlinePreviewHtml('');
                                        }} 
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                {resumeLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, padding: '40px' }}>
                                        <div className="spinner"></div><p style={{marginLeft: 10, color: '#666'}}>Loading preview...</p>
                                    </div>
                                ) : inlinePreviewHtml ? (
                                    <div 
                                        dangerouslySetInnerHTML={{ __html: inlinePreviewHtml }} 
                                        style={{ padding: '20px', overflowY: 'auto', flex: 1 }}
                                    />
                                ) : inlinePreviewUrl ? (
                                    <iframe 
                                        src={inlinePreviewUrl} 
                                        title="Resume Preview"
                                        style={{ width: '100%', flex: 1, border: 'none' }}
                                    />
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };   

    if (isLoading) {
        if (isModal) {
            return (
                <div 
                    className="onb-role-modal-overlay" 
                    onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                >
                    <div 
                        className="onb-role-modal-dialog"
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            padding: '48px 32px',
                            textAlign: 'center',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '16px',
                            minWidth: '320px'
                        }}
                    >
                        <div className="spinner" style={{ width: '42px', height: '42px', border: '4px solid #e2e8f0', borderTop: '4px solid #3b5bfd', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        <p style={{ margin: 0, color: '#475569', fontWeight: 600, fontSize: '0.95rem' }}>Loading role details...</p>
                        <button type="button" onClick={onClose} style={{ marginTop: '8px', padding: '6px 16px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>Cancel</button>
                    </div>
                </div>
            );
        }
        return <div className="role-detail-loading"><div className="spinner"></div><p>Loading...</p></div>;
    }

    if (!role) {
        if (isModal) {
            return (
                <div 
                    className="onb-role-modal-overlay" 
                    onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                >
                    <div 
                        className="onb-role-modal-dialog"
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            padding: '36px 32px',
                            textAlign: 'center',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                            minWidth: '320px'
                        }}
                    >
                        <p style={{ margin: 0, color: '#ef4444', fontWeight: 600, fontSize: '1rem' }}>Role details not found.</p>
                        <button type="button" onClick={onClose} style={{ marginTop: '8px', padding: '8px 22px', background: '#3b5bfd', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Close</button>
                    </div>
                </div>
            );
        }
        return <div className="role-detail-error">Role not found.</div>;
    }

    const hiringStepCounts = calculateHiringStepCounts(applications);

    const pageContent = (
        <div className="role-detail-page-container" style={isModal ? { padding: '16px 20px', maxWidth: '100%', margin: 0, background: '#f8fafc' } : undefined}>
            <div className="role-detail-top-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button className="back-link-btn" onClick={() => isModal && onClose ? onClose() : navigate(-1)}><ChevronLeft size={18} /> Back</button>
                <div className="top-nav-actions" style={{ display: 'flex', gap: '10px' }}>
                    {isEditing ? (
                        <>
                            <button onClick={handleCancelEdit} style={{ padding: '6px 12px', border: '1px solid #ef4444', color: '#ef4444', backgroundColor: 'transparent', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                            <button onClick={handleSaveChanges} style={{ padding: '6px 12px', backgroundColor: '#3b5bfd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Save Changes</button>
                        </>
                    ) : (
                        <button onClick={handleEditClick} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', backgroundColor: '#3b5bfd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>
                            <FileText size={14} /> Edit
                        </button>
                    )}
                </div>
            </div>

            <div className="role-detail-main-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div className="title-section">
                    {isEditing ? (
                        <input 
                            type="text" 
                            name="role" 
                            value={editFormData.role || ''} 
                            onChange={handleInputChange} 
                            className="edit-input-title"
                            style={{ fontSize: '24px', fontWeight: '700', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 8px', width: '100%', maxWidth: '500px' }}
                        />
                    ) : (
                        <h1>{role.role}</h1>
                    )}
                    {!isEditing && (
                        <div className="badge-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span className={`status-badge ${role.status?.toLowerCase().replace(/\s+/g, '-')}`}>{role.status}</span>
                            <span className={`urgency-badge ${role.urgency?.toLowerCase()}`}>{role.urgency}</span>
                        </div>
                    )}
                </div>
                {!isEditing && (
                    <div className="ats-view-toggle">
                        <button 
                            className={`ats-toggle-btn ${activeView === 'details' ? 'active' : ''}`}
                            onClick={() => setActiveView('details')}
                        >
                            <Table size={18} />
                            <span>Role Details</span>
                        </button>
                        <button 
                            className={`ats-toggle-btn ${activeView === 'analyzer' ? 'active' : ''}`}
                            onClick={() => setActiveView('analyzer')}
                        >
                            <Zap size={18} />
                            <span>Resume Analyzer</span>
                        </button>
                    </div>
                )}
            </div>

            {activeView === 'details' ? (
                <>
                <div className="rd-bento-grid">
                <div className="rd-bento-item rd-summary-box">
                    <div className="card-header">
                        <h2>Role Summary</h2>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button className="source-candidate-btn" onClick={() => setShowSourceCandidateModal(true)}>Source Candidate</button>
                            {/* <button className="recruitment-submit-resume-btn" onClick={() => setShowResumeSubmissionModal(true)}>Submit Resume</button> */}
                        </div>
                    </div>
                    <div className="pipeline-card-box">
                        <div className="pipeline-card-header">
                            <span className="pipeline-card-title">PIPELINE</span>
                            <span className="pipeline-card-total">Total: {applications.length}</span>
                        </div>
                        <div className="pipeline-horizontal-list">
                            {Object.entries(hiringStepCounts).map(([name, count]) => (
                                <div key={name} className="pipeline-stat-card" title={`${name}: ${count}`}>
                                    <span className="pipeline-stat-count">{count}</span>
                                    <span className="pipeline-stat-name">{name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="details-section">
                        <div className="detail-item">
                            <div className="detail-label"><Hash size={14} /><label>External Job Ref</label></div>
                            {isEditing ? <input type="text" name="jobId" value={editFormData.jobId || ''} onChange={handleInputChange} className="edit-input" /> : <span>{role.jobId || 'N/A'}</span>}
                        </div>
                        {role.roleCategory?.trim().toLowerCase() !== 'offshore' && (
                            <div className="detail-item">
                                <div className="detail-label"><Layers size={14} /><label>GBAMS ID</label></div>
                                {isEditing ? (
                                    <input type="text" name="gbamsId" value={editFormData.gbamsId || ''} onChange={handleInputChange} className="edit-input" />
                                ) : (
                                    <span>
                                        {(!role.gbamsId || 
                                          String(role.gbamsId).toLowerCase().includes('beeline') || 
                                          String(role.gbamsId).toLowerCase() === 'n/a') 
                                            ? 'N/A' 
                                            : role.gbamsId}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="detail-item">
                            <div className="detail-label"><Activity size={14} /><label>System ID</label></div>
                            <span>JOB-{String(role.id).padStart(5, '0')}</span>
                        </div>
                        <div className="detail-item" data-tooltip={role.role} title={role.role || ''}>
                            <div className="detail-label"><Briefcase size={14} /><label>Role Title</label></div>
                            {isEditing ? (
                                <input type="text" name="role" value={editFormData.role || ''} onChange={handleInputChange} className="edit-input" />
                            ) : (
                                <span title={role.role || ''}>
                                    {role.role ? (role.role.length > 15 ? `${role.role.slice(0, 15)}...` : role.role) : 'N/A'}
                                </span>
                            )}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Activity size={14} /><label>Role Type</label></div>
                            {isEditing ? (
                                <select name="roleType" value={editFormData.roleType || ''} onChange={handleInputChange} className="edit-input">
                                    <option value="">Select</option>
                                    <option value="W2">W2</option>
                                    <option value="C2C">C2C</option>
                                    <option value="1099">1099</option>
                                    <option value="Full Time">Full Time</option>
                                    <option value="Contract">Contract</option>
                                </select>
                            ) : <span>{role.roleType || 'N/A'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Calendar size={14} /><label>Created</label></div>
                            <span>{formatDateTime(role.createdAt || role.created_at)}</span>
                        </div>
                        {(() => {
                            if (!role || !role.client || !role.client.trim()) {
                                return (
                                    <div className="detail-item">
                                        <div className="detail-label"><Building size={14} /><label>Client</label></div>
                                        <span>N/A</span>
                                    </div>
                                );
                            }

                            const rawClient = role.client.trim().toLowerCase();

                            // Detect Job Region (IN vs US)
                            const locStr = `${role.country || ''} ${role.location || ''} ${role.city || ''} ${role.state || ''} ${role.roleLocation || ''}`.toLowerCase();
                            const isIndiaJob = locStr.includes('india') || locStr.includes(', in') || locStr.endsWith(' in') || locStr.includes('in,') || locStr === 'in';
                            const isUSJob = locStr.includes('us') || locStr.includes('usa') || locStr.includes('united states');

                            // Strip region suffixes from raw client (e.g. "tcs (in)" -> "tcs", "tcs (us)" -> "tcs")
                            const cleanBaseClient = rawClient
                                .replace(/\s*\((in|us|usa|india)\)/gi, '')
                                .replace(/\b(in|us|usa|india)\b/gi, '')
                                .trim();

                            // Find candidate matches across active CRM companies
                            const candidateMatches = crmCompanies.filter(c => {
                                const disp = (c.DisplayName || '').trim().toLowerCase();
                                const comp = (c.CompanyName || '').trim().toLowerCase();
                                const code = (c.CompanyCode || '').trim().toLowerCase();

                                const dispBase = disp.replace(/\s*\((in|us|usa|india)\)/gi, '').replace(/\b(in|us|usa|india)\b/gi, '').trim();
                                const compBase = comp.replace(/\s*\((in|us|usa|india)\)/gi, '').replace(/\b(in|us|usa|india)\b/gi, '').trim();

                                return disp === rawClient || comp === rawClient || code === rawClient ||
                                       dispBase === cleanBaseClient || compBase === cleanBaseClient ||
                                       (cleanBaseClient.length >= 2 && (dispBase.includes(cleanBaseClient) || cleanBaseClient.includes(dispBase))) ||
                                       (cleanBaseClient.length >= 2 && (compBase.includes(cleanBaseClient) || cleanBaseClient.includes(compBase)));
                            });

                            let matchedCrm = crmCompanies.find(c => {
                                const disp = (c.DisplayName || '').trim().toLowerCase();
                                const comp = (c.CompanyName || '').trim().toLowerCase();
                                const code = (c.CompanyCode || '').trim().toLowerCase();
                                return disp === rawClient || comp === rawClient || code === rawClient;
                            });

                            if (!matchedCrm) {
                                if (isUSJob) {
                                    matchedCrm = candidateMatches.find(c => {
                                        const text = `${c.DisplayName || ''} ${c.CompanyName || ''} ${c.Country || ''}`.toLowerCase();
                                        return text.includes('(us)') || text.includes('us') || text.includes('usa') || text.includes('united states');
                                    });
                                } else if (isIndiaJob) {
                                    matchedCrm = candidateMatches.find(c => {
                                        const text = `${c.DisplayName || ''} ${c.CompanyName || ''} ${c.Country || ''}`.toLowerCase();
                                        return text.includes('india') || text.includes('(in)') || text.includes(' in');
                                    });
                                }
                            }

                            if (!matchedCrm && candidateMatches.length > 0) {
                                matchedCrm = candidateMatches[0];
                            }

                            let displayClientName = role.client || 'N/A';
                            let hoverText = role.client || 'N/A';

                            if (matchedCrm) {
                                const disp = (matchedCrm.DisplayName || '').trim();
                                hoverText = matchedCrm.CompanyCode ? `${role.client || disp} (${matchedCrm.CompanyCode})` : (role.client || disp);
                            }

                            return (
                                <div className="detail-item" data-tooltip={hoverText}>
                                    <div className="detail-label"><Building size={14} /><label>Client</label></div>
                                    {isEditing ? (
                                        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', width: '200px', maxWidth: '200px' }}>
                                            <input
                                                type="text"
                                                name="client"
                                                value={editFormData.client !== undefined ? editFormData.client : (role.client || '')}
                                                onChange={handleInputChange}
                                                onFocus={(e) => {
                                                    const dropdown = e.target.nextElementSibling;
                                                    if (dropdown) dropdown.style.display = 'block';
                                                }}
                                                onBlur={(e) => {
                                                    setTimeout(() => {
                                                        const dropdown = e.target.nextElementSibling;
                                                        if (dropdown) dropdown.style.display = 'none';
                                                    }, 200);
                                                }}
                                                placeholder="Select or type to filter client"
                                                className="edit-input"
                                                autoComplete="off"
                                                style={{ width: '100%' }}
                                            />
                                            <div className="recruitment-dropdown-options" style={{ display: 'none', position: 'absolute', top: '100%', right: 0, width: '100%', minWidth: '220px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                                {(crmCompanies && crmCompanies.length > 0
                                                    ? crmCompanies.map(c => {
                                                        const disp = (c.DisplayName || c.CompanyName || '').trim();
                                                        const legal = (c.CompanyName || '').trim();
                                                        return {
                                                            displayName: disp,
                                                            companyName: legal && legal !== disp ? legal : ''
                                                        };
                                                    }).filter(item => item.displayName)
                                                    : [
                                                        { displayName: 'TCS (US)', companyName: 'Tata Consultancy Services' },
                                                        { displayName: 'Wipro (US)', companyName: 'Wipro Limited' },
                                                        { displayName: 'Infosys (US)', companyName: 'Infosys Limited' },
                                                        { displayName: 'Capgemini (US)', companyName: 'Capgemini SE' },
                                                        { displayName: 'Cognizant (US)', companyName: 'Cognizant Technology Solutions' }
                                                    ]
                                                )
                                                .filter((item, idx, self) => self.findIndex(s => s.displayName === item.displayName) === idx)
                                                .filter(clientObj => {
                                                    const search = (editFormData.client || '').toLowerCase();
                                                    return clientObj.displayName.toLowerCase().includes(search) || 
                                                           clientObj.companyName.toLowerCase().includes(search);
                                                })
                                                .map((clientObj, index) => (
                                                    <div
                                                        key={index}
                                                        className="recruitment-dropdown-option"
                                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', textAlign: 'left' }}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setEditFormData(prev => ({ ...prev, client: clientObj.displayName }));
                                                            const dropdown = e.currentTarget.parentElement;
                                                            if (dropdown) dropdown.style.display = 'none';
                                                        }}
                                                    >
                                                        <span className="recruitment-option-text" style={{ fontWeight: 600, color: '#1f2937' }}>
                                                            {clientObj.displayName}
                                                        </span>
                                                        {clientObj.companyName && (
                                                            <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '12px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                {clientObj.companyName}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}

                                                <div
                                                    className="recruitment-dropdown-option"
                                                    style={{
                                                        borderTop: '1px solid #e5e7eb',
                                                        color: '#3b5bfd',
                                                        fontWeight: 600,
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        backgroundColor: '#f9fafb',
                                                        textAlign: 'left'
                                                    }}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        const clientToAdd = (editFormData.client || '').trim();
                                                        handleQuickAddClientToCrm(clientToAdd);
                                                    }}
                                                >
                                                    + Add {(editFormData.client || '').trim() ? `"${(editFormData.client || '').trim()}"` : "New Client"} to CRM
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <span>{displayClientName}</span>
                                    )}
                                </div>
                            );
                        })()}
                        <div className="detail-item">
                            <div className="detail-label"><User size={14} /><label>Client POC</label></div>
                            {isEditing ? <input type="text" name="clientPocName" value={editFormData.clientPocName || ''} onChange={handleInputChange} className="edit-input" /> : <span>{role.clientPocName || 'N/A'}</span>}
                        </div>
                        <div className="detail-item" data-tooltip={formatLocation(isEditing ? editFormData : role)}>
                            <div className="detail-label"><MapPin size={14} /><label>Location</label></div>
                            {isEditing ? <input type="text" name="location" value={editFormData.location || ''} onChange={handleInputChange} className="edit-input" placeholder="City, State" /> : <span>{formatLocation(role).length > 35 ? formatLocation(role).substring(0, 35) + '...' : formatLocation(role)}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Globe size={14} /><label>Work Mode</label></div>
                            {isEditing ? (
                                <select name="roleLocation" value={editFormData.roleLocation || ''} onChange={handleInputChange} className="edit-input">
                                    <option value="">Select</option>
                                    <option value="Remote">Remote</option>
                                    <option value="Onsite">Onsite</option>
                                    <option value="Hybrid">Hybrid</option>
                                </select>
                            ) : <span>{role.roleLocation || 'N/A'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><MapPin size={14} /><label>Role Category</label></div>
                            {isEditing ? (
                                <select name="roleCategory" value={editFormData.roleCategory || ''} onChange={handleInputChange} className="edit-input">
                                    <option value="">Select</option>
                                    <option value="Onshore">Onshore</option>
                                    <option value="Offshore">Offshore</option>
                                </select>
                            ) : <span>{role.roleCategory?.trim().toLowerCase() === 'offshore' ? 'Offshore' : 'Onsite'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Award size={14} /><label>Experience</label></div>
                            {isEditing ? <input type="text" name="experience" value={editFormData.experience || ''} onChange={handleInputChange} className="edit-input" /> : <span>{role.experience || 'N/A'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><DollarSign size={14} /><label>Rate Range</label></div>
                            {isEditing ? (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <input type="number" name="minRate" value={editFormData.minRate || ''} onChange={handleInputChange} className="edit-input" style={{ width: '50px' }} placeholder="Min" />
                                    -
                                    <input type="number" name="maxRate" value={editFormData.maxRate || ''} onChange={handleInputChange} className="edit-input" style={{ width: '50px' }} placeholder="Max" />
                                    <select name="currency" value={editFormData.currency || ''} onChange={handleInputChange} className="edit-input" style={{ width: '60px', padding: '0 2px' }}>
                                        <option value="USD">USD</option>
                                        <option value="INR">INR</option>
                                        <option value="CAD">CAD</option>
                                        <option value="EUR">EUR</option>
                                        <option value="GBP">GBP</option>
                                    </select>
                                </div>
                            ) : (
                                <span>
                                    {role.minRate !== undefined && role.minRate !== null && role.maxRate !== undefined && role.maxRate !== null 
                                        ? `${formatCurrency(role.minRate, role.currency)} - ${formatCurrency(role.maxRate, role.currency)}${role.currency === 'INR' ? '/month' : '/hr'}` 
                                        : 'N/A'}
                                </span>
                            )}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Users size={14} /><label>Vendor</label></div>
                            {isEditing ? <input type="text" name="vendorName" value={editFormData.vendorName || ''} onChange={handleInputChange} className="edit-input" /> : <span>{role.vendorName || 'N/A'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Globe size={14} /><label>Visa Types</label></div>
                            {isEditing ? <input type="text" name="visaTypes" value={editFormData.visaTypes || ''} onChange={handleInputChange} className="edit-input" placeholder="e.g. H1B, USC, GC" /> : <span>{role.visaTypes || 'N/A'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Clock size={14} /><label>Urgency</label></div>
                            {isEditing ? (
                                <select name="urgency" value={editFormData.urgency || ''} onChange={handleInputChange} className="edit-input">
                                    <option value="Normal">Normal</option>
                                    <option value="High">High</option>
                                    <option value="Critical">Critical</option>
                                </select>
                            ) : <span className={`urgency-text ${role.urgency?.toLowerCase()}`}>{role.urgency || 'Normal'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Activity size={14} /><label>Status</label></div>
                            {isEditing ? (
                                <select name="status" value={editFormData.status || ''} onChange={handleInputChange} className="edit-input">
                                    <option value="Active">Active</option>
                                    <option value="Draft">Draft</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Modified">Modified</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="Cancelled">Cancelled</option>
                                    <option value="Filled">Filled</option>
                                    <option value="Bench">Bench</option>
                                </select>
                            ) : <span className={`status-text ${role.status?.toLowerCase().replace(/\s+/g, '-')}`}>{role.status || 'Active'}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Calendar size={14} /><label>Start Date</label></div>
                            {isEditing ? <input type="date" name="startDate" value={editFormData.startDate || ''} onChange={handleInputChange} className="edit-input" /> : <span>{formatDateOnly(role.startDate)}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><User size={14} /><label>Role Owner</label></div>
                            <span>{formatRoleOwner(role.assignTo)}</span>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Calendar size={14} /><label>End Date</label></div>
                            {isEditing ? <input type="date" name="endDate" value={editFormData.endDate || ''} onChange={handleInputChange} className="edit-input" /> : <span>{formatDateOnly(role.endDate)}</span>}
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><Users size={14} /><label>Assigned Recruiters</label></div>
                            <span>{getAssignedRecruiters(role.assignedRecruiters)}</span>
                        </div>
                        <div className="detail-item">
                            <div className="detail-label"><User size={14} /><label>Created By</label></div>
                            <span>{formatRoleOwner(role.createdBy) || 'N/A'}</span>
                        </div>

                        {/* ── PLACEMENT CHAIN SECTION (Job Baseline Chain) ── */}
                        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: '18px', marginTop: '8px' }}>
                            <PlacementChainDisplay
                                jobId={role.id}
                                entityType="job"
                                isEditMode={isEditing}
                                clientName={isEditing ? editFormData.client : (role.client || '')}
                                vendorName={isEditing ? editFormData.vendorName : role.vendorName}
                                showSupplierCandidate={false}
                                relatedJobId={role.id}
                                hasApplications={applications && applications.length > 0}
                                onSyncApplications={fetchRoleData}
                            />
                        </div>
                    </div>
                </div>

                <div className="rd-bento-item rd-description-box">
                    <div className="card-header"><h2>Job Description</h2></div>
                    {isEditing ? (
                        <div
                            ref={jobDescEditorRef}
                            contentEditable
                            suppressContentEditableWarning
                            className="description-content-full"
                            onInput={(e) => {
                                const html = e.currentTarget.innerHTML; // capture before React nullifies event
                                setEditFormData(prev => ({ ...prev, jobDescription: html }));
                            }}
                            style={{
                                minHeight: '300px',
                                padding: '12px',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0',
                                outline: 'none',
                                overflowY: 'auto',
                                cursor: 'text',
                                lineHeight: '1.7',
                                color: '#475569',
                                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.04)'
                            }}
                        />
                    ) : (
                        <div className="description-content-full" dangerouslySetInnerHTML={{ __html: role.jobDescription || 'No description.' }} />
                    )}
                </div>

                <div className="rd-bento-item rd-candidates-box">
                    <div className="section-header"><h2>Applications ({applications.length})</h2></div>
                    {applications.length === 0 ? (
                        <div className="no-candidates-state"><h3>No Applications</h3></div>
                    ) : (
                        <div className="candidates-list">
                            {applications.map(app => (
                                <div key={app.id} className="candidate-card">
                                    <div className="candidate-header">
                                        <div className="candidate-info-wrapper">
                                            <div className="candidate-avatar">{getInitials(app.name)}</div>
                                            <div className="candidate-info">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <h3 style={{ margin: 0 }}>{app.name}</h3>
                                                    {Boolean(app.isBench || app.is_bench) && (
                                                        <span style={{
                                                            background: '#ffedd5',
                                                            color: '#c2410c',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '11px',
                                                            fontWeight: 700,
                                                            border: '1px solid #fed7aa',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '3px'
                                                        }}>
                                                            Bench Profile
                                                        </span>
                                                    )}
                                                </div>
                                                <p style={{ marginTop: '2px', marginBottom: 0 }}>{app.email} | {app.phone}</p>
                                            </div>
                                        </div>
                                        <div className="candidate-actions">
                                            <button className="edit-btn" onClick={(e) => openEditApplicationModal(app, e)}>Edit</button>
                                            <button className="delete-btn" onClick={(e) => deleteApplication(app.id, app.name, e)}><Trash2 size={16} /></button>
                                            <button className="expand-btn" onClick={(e) => toggleCandidateExpand(app.id, e)}>{expandedCandidateId === app.id ? 'Less ▲' : 'More ▼'}</button>
                                        </div>
                                    </div>

                                    {/* Candidate-Specific Placement Chain */}
                                    <div id={`placement-chain-app-${app.id}`} style={{ marginTop: '6px', marginBottom: '6px', padding: '6px 10px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', transition: 'all 0.3s ease' }}>
                                        <PlacementChainDisplay
                                            jobId={app.id}
                                            entityType="submission"
                                            isEditMode={editingChainAppId === app.id}
                                            clientName={isEditing ? editFormData.client : (role.client || '')}
                                            vendorName={role.vendorName}
                                            candidateName={app.name}
                                            showSupplierCandidate={true}
                                            relatedJobId={role.id}
                                            onChainSaved={() => setEditingChainAppId(null)}
                                            onCancelEdit={() => setEditingChainAppId(null)}
                                        />
                                    </div>

                                    <div className="hiring-pipeline-view">
                                        <div className="pipeline-header" onClick={(e) => toggleHiringProcess(app.id, e)}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                {(() => {
                                                    const sLower = (app.status || '').toLowerCase();
                                                    const isRej = sLower === 'rejected';
                                                    const isWdr = sLower === 'withdrawn';
                                                    const isHld = sLower === 'hold' || sLower === 'on_hold' || sLower === 'on hold';
                                                    const matchedDef = (app.currentStage && hiringSteps.find(s => s.stageCode === app.currentStage)) || hiringSteps[app.currentStep];
                                                    const stageName = app.rejectedAtStage || matchedDef?.stepName || app.currentStage || 'Stage';
                                                    if (isRej) {
                                                        return (
                                                            <span style={{ color: '#ef4444', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                <strong>Status:</strong> REJECTED – {stageName}
                                                                {app.rejectedReason && (
                                                                    <span style={{ fontWeight: 500, fontSize: '11px', color: '#991b1b', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px', border: '1px solid #fca5a5' }}>
                                                                        Note: {app.rejectedReason}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        );
                                                    }
                                                    if (isWdr) {
                                                        return (
                                                            <span style={{ color: '#d97706', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                <strong>Status:</strong> WITHDRAWN – {stageName}
                                                                {app.withdrawnReason && (
                                                                    <span style={{ fontWeight: 500, fontSize: '11px', color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: '4px', border: '1px solid #fde68a' }}>
                                                                        Note: {app.withdrawnReason}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        );
                                                    }
                                                    if (isHld) {
                                                        return (
                                                            <span style={{ color: '#7c3aed', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                <strong>Status:</strong> ON HOLD – {stageName}
                                                                {app.notes && (
                                                                    <span style={{ fontWeight: 500, fontSize: '11px', color: '#6d28d9', background: '#ede9fe', padding: '2px 8px', borderRadius: '4px', border: '1px solid #c4b5fd' }}>
                                                                        Note: {app.notes}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handleUnholdCandidate(app.id, app, null, e); }}
                                                                    style={{
                                                                        marginLeft: '4px',
                                                                        padding: '2px 8px',
                                                                        background: '#3b5bfd',
                                                                        color: '#ffffff',
                                                                        border: 'none',
                                                                        borderRadius: '12px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 700,
                                                                        cursor: 'pointer',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                                    }}
                                                                    title={`Unhold ${app.name} and resume active status`}
                                                                >
                                                                    <PlayCircle size={11} /> Unhold
                                                                </button>
                                                            </span>
                                                        );
                                                    }
                                                    return <span><strong>Status:</strong> {app.status || 'Active'}</span>;
                                                })()}
                                                {(() => {
                                                    const matchedDef = (app.currentStage && hiringSteps.find(s => s.stageCode === app.currentStage)) || hiringSteps[app.currentStep];
                                                    const stepTimestampObj = (app.currentStage && app.stageTimestampsByCode?.[app.currentStage]) || app.stageTimestamps?.[app.currentStep];
                                                    if (!stepTimestampObj?.timestamp) return null;
                                                    const displayStageName = matchedDef?.stepName || app.currentStage || 'Stage';
                                                    const isRej = (app.status || '').toLowerCase() === 'rejected';
                                                    const isWdr = (app.status || '').toLowerCase() === 'withdrawn';
                                                    const isHld = (app.status || '').toLowerCase() === 'hold' || (app.status || '').toLowerCase() === 'on_hold' || (app.status || '').toLowerCase() === 'on hold';
                                                    return (
                                                        <span style={{
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            color: isRej ? '#b91c1c' : isWdr ? '#b45309' : isHld ? '#7c3aed' : '#2563eb',
                                                            background: isRej ? '#fee2e2' : isWdr ? '#fef3c7' : isHld ? '#ede9fe' : '#eff3ff',
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            border: `1px solid ${isRej ? '#fca5a5' : isWdr ? '#fde68a' : isHld ? '#c4b5fd' : '#bfdbfe'}`,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            <Clock size={12} />
                                                            {displayStageName}: {formatDateTime(stepTimestampObj.timestamp)}
                                                            {stepTimestampObj.changedBy ? ` (${stepTimestampObj.changedBy})` : ''}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <span>{showHiringProcess[app.id] ? '▲' : '▼'}</span>
                                        </div>
                                        {showHiringProcess[app.id] && <div className="pipeline-steps">{generateHiringSteps(app)}</div>}
                                    </div>
                                    {expandedCandidateId === app.id && renderExpandedDetails(app)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            </>
            ) : (
                <div className="resume-analyzer-wrapper">
                    <ResumeAnalyzer 
                        initialJobDescription={role?.jobDescription || ''} 
                        onCancel={() => setActiveView('details')} 
                    />
                </div>
            )}

            {showResumeModal && (
                <div className="recruitment-modal" onClick={() => setShowResumeModal(false)}>
                    <div className="recruitment-modal-content" style={{ maxWidth: '1000px', height: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        <div className="recruitment-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2>Resume: {currentCandidateName}</h2>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                {(currentFileType === 'docx' || currentFileType === 'doc') && (
                                    <button 
                                        onClick={async () => {
                                            try {
                                                const token = localStorage.getItem('token');
                                                const response = await axios.get(currentDownloadUrl, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                    responseType: 'blob'
                                                });
                                                const blob = new Blob([response.data]);
                                                const link = document.createElement('a');
                                                link.href = window.URL.createObjectURL(blob);
                                                link.download = currentCandidateName;
                                                link.click();
                                            } catch (err) {
                                                console.error("Download failed:", err);
                                            }
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#3b5bfd',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Download Original
                                    </button>
                                )}
                                <span 
                                    className="recruitment-close" 
                                    onClick={() => setShowResumeModal(false)} 
                                    style={{ 
                                        position: 'static', 
                                        fontSize: '24px', 
                                        cursor: 'pointer',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white'
                                    }}
                                >
                                    &times;
                                </span>
                            </div>
                        </div>
                        <div className="recruitment-modal-body" style={{ height: 'calc(100% - 80px)', padding: 0, overflowY: 'auto' }}>
                            {currentFileType === 'docx' || currentFileType === 'doc' ? (
                                <div style={{ padding: '25px', backgroundColor: '#f8f9fa' }}>
                                    <div 
                                        dangerouslySetInnerHTML={{ __html: currentResumeHtml }} 
                                        style={{
                                            backgroundColor: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                                            padding: '40px',
                                            maxWidth: '800px',
                                            margin: '0 auto',
                                            border: '1px solid #e2e8f0'
                                        }}
                                    />
                                </div>
                            ) : (
                                <iframe src={currentResumeUrl} width="100%" height="100%" title="Resume" />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ResumeSubmissionModal selectedRole={role} showResumeSubmissionModal={showResumeSubmissionModal} closeResumeSubmissionModal={() => setShowResumeSubmissionModal(false)} fetchApplications={fetchApplications} showNotification={() => {}} refreshRolesData={() => {}} />
            <EditApplicationModal showEditApplicationModal={showEditApplicationModal} applicationToEdit={applicationToEdit} closeEditApplicationModal={closeEditApplicationModal} fetchApplications={fetchApplications} selectedRole={role} showNotification={() => {}} applicationResumes={applicationResumes} fetchApplicationResumes={fetchApplicationResumes} viewResume={viewResume} />
            <SourceCandidateModal isOpen={showSourceCandidateModal} onClose={() => setShowSourceCandidateModal(false)} role={role} onProfileSubmitted={fetchApplications} applications={applications} />
            {showAddCompanyModal && (
                <AddCompanyModal
                    onClose={() => setShowAddCompanyModal(false)}
                    onSuccess={handleCompanyCreated}
                    companyData={quickAddCompanyData}
                />
            )}
        </div>
    );

    if (isModal) {
        return (
            <div 
                className="onb-role-modal-overlay" 
                onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}
            >
                <div 
                    className="onb-role-modal-dialog"
                    style={{
                        backgroundColor: '#f8fafc',
                        borderRadius: '14px',
                        width: '96%',
                        maxWidth: '1380px',
                        maxHeight: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                >
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        {pageContent}
                    </div>
                </div>
            </div>
        );
    }

    return pageContent;
};

export default RoleDetailView;




// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import Swal from 'sweetalert2';
// import BASE_URL from '../../url';
// import '../styles/erprecruitment.css';
// import '../styles/RoleDetailView.css';
// import { 
//     ChevronLeft, 
//     Building, 
//     MapPin, 
//     Hash,
//     Layers,
//     XCircle,
//     FileText,
//     Trash2,
//     Activity,
//     Briefcase,
//     Calendar,
//     User,
//     Globe,
//     Award,
//     DollarSign,
//     Users,
//     Clock
// } from 'lucide-react';
// import { formatLocation } from '../config/locationConfig';
// import ResumeSubmissionModal from './ResumeSubmissionModal';
// import EditApplicationModal from './EditApplicationModal';
// import SourceCandidateModal from './SourceCandidateModal';

// const RoleDetailView = () => {
//     const { roleId } = useParams();
//     const navigate = useNavigate();
//     const [role, setRole] = useState(null);
//     const [applications, setApplications] = useState([]);
//     const [isLoading, setIsLoading] = useState(true);
//     const [hiringSteps, setHiringSteps] = useState([]);
//     const [applicationResumes, setApplicationResumes] = useState({});
//     const [applicationNotes, setApplicationNotes] = useState({});
//     const [expandedCandidateId, setExpandedCandidateId] = useState(null);
//     const [showHiringProcess, setShowHiringProcess] = useState({});
//     const [resumeLoading, setResumeLoading] = useState(false);
//     const [showResumeModal, setShowResumeModal] = useState(false);
//     const [currentResumeUrl, setCurrentResumeUrl] = useState('');
//     const [currentCandidateName, setCurrentCandidateName] = useState('');
//     const [userRole, setUserRole] = useState(localStorage.getItem('role'));

//     const [showResumeSubmissionModal, setShowResumeSubmissionModal] = useState(false);
//     const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);
//     const [showSourceCandidateModal, setShowSourceCandidateModal] = useState(false);
//     const [applicationToEdit, setApplicationToEdit] = useState(null);

//     const formatCurrency = (amount, currencyCode) => {
//         if (amount === undefined || amount === null) return 'N/A';
//         const symbols = { 'INR': '₹', 'USD': '$', 'CAD': '$', 'EUR': '€', 'GBP': '£' };
//         return `${symbols[currencyCode] || currencyCode}${amount.toLocaleString()}`;
//     };

//     const formatDateTime = (dateStr) => {
//         if (!dateStr) return 'N/A';
//         return new Date(dateStr).toLocaleString();
//     };

//     const fetchApplicationResumes = useCallback(async (applicationId) => {
//         try {
//             const token = localStorage.getItem('token');
//             const response = await axios.get(`${BASE_URL}/api/recruitment/applications/${applicationId}/resumes`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             setApplicationResumes(prev => ({ ...prev, [applicationId]: response.data }));
//         } catch (error) {
//             console.error(`Error fetching resumes:`, error);
//         }
//     }, []);

//     const fetchApplicationStepNotes = useCallback(async (applicationId) => {
//         try {
//             const token = localStorage.getItem('token');
//             const response = await axios.get(`${BASE_URL}/api/recruitment/applications/${applicationId}/step-notes`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             const notesMap = {};
//             response.data.forEach(note => { notesMap[note.stepIndex] = note.notes; });
//             setApplicationNotes(prev => ({ ...prev, [applicationId]: notesMap }));
//         } catch (error) {
//             console.error(`Error fetching notes:`, error);
//         }
//     }, []);

//     const fetchApplications = useCallback(async () => {
//         try {
//             const token = localStorage.getItem('token');
//             const response = await axios.get(`${BASE_URL}/api/recruitment/applications/role/${roleId}/with-resumes`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             setApplications(response.data);
            
//             for (const app of response.data) {
//                 if (app.resumeCount > 0) fetchApplicationResumes(app.id);
//                 fetchApplicationStepNotes(app.id);
//             }
//         } catch (error) {
//             console.error('Error fetching applications:', error);
//         }
//     }, [roleId, fetchApplicationResumes, fetchApplicationStepNotes]);

//     const fetchRoleData = useCallback(async () => {
//         try {
//             setIsLoading(true);
//             const token = localStorage.getItem('token');
//             const response = await axios.get(`${BASE_URL}/api/recruitment/roles/${roleId}`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             setRole(response.data);
//             await fetchApplications();
            
//             const stepsResponse = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             setHiringSteps(stepsResponse.data);
//         } catch (error) {
//             console.error('Error fetching role details:', error);
//         } finally {
//             setIsLoading(false);
//         }
//     }, [roleId, fetchApplications]);

//     useEffect(() => {
//         fetchRoleData();
//     }, [fetchRoleData]);

//     const openEditApplicationModal = useCallback((app, e) => {
//         if (e) e.stopPropagation();
//         setApplicationToEdit(app);
//         setShowEditApplicationModal(true);
//     }, []);

//     const closeEditApplicationModal = useCallback(() => {
//         setShowEditApplicationModal(false);
//         setApplicationToEdit(null);
//     }, []);

//     const calculateHiringStepCounts = (apps) => {
//         const steps = hiringSteps.length > 0 ? hiringSteps : [];
//         const counts = {};
//         steps.forEach(s => { counts[s.stepName] = 0; });
//         apps.forEach(app => {
//             const step = steps[app.currentStep];
//             if (step) counts[step.stepName]++;
//         });
//         return counts;
//     };

//     const toggleCandidateExpand = (appId, e) => {
//         e.stopPropagation();
//         setExpandedCandidateId(expandedCandidateId === appId ? null : appId);
//     };

//     const toggleHiringProcess = (applicationId, e) => {
//         e.stopPropagation();
//         setShowHiringProcess(prev => ({ ...prev, [applicationId]: !prev[applicationId] }));
//     };

//     const viewResume = async (resumeId, applicationId, e) => {
//         e.stopPropagation();
//         try {
//             setResumeLoading(true);
//             const token = localStorage.getItem('token');
//             const response = await axios.get(`${BASE_URL}/api/recruitment/applications/${applicationId}/resume/${resumeId}`, {
//                 headers: { Authorization: `Bearer ${token}` },
//                 responseType: 'blob'
//             });

//             if (!response.data || response.data.size === 0) {
//               throw new Error('Received an empty resume file');
//             }
            
//             const fileURL = URL.createObjectURL(response.data);
//             setCurrentResumeUrl(fileURL);
//             const app = applications.find(a => a.id === applicationId);
//             setCurrentCandidateName(app ? app.name : 'Candidate');
//             setShowResumeModal(true);
//         } catch (error) {
//             console.error('Error viewing resume:', error);
//             Swal.fire({
//                 title: 'Error Rendering PDF',
//                 text: 'The resume file appears to be corrupted or could not be retrieved from the database.',
//                 icon: 'error'
//             });
//         } finally {
//             setResumeLoading(false);
//         }
//     };

//     const advanceStep = async (applicationId, currentStepIndex, e) => {
//         if (e) e.stopPropagation();
//         const result = await Swal.fire({
//             title: 'Advance Candidate?',
//             text: "Move this candidate to the next step?",
//             icon: 'question',
//             showCancelButton: true,
//             confirmButtonColor: '#3b5bfd',
//             confirmButtonText: 'Yes, advance'
//         });
//         if (!result.isConfirmed) return;
//         try {
//             const token = localStorage.getItem('token');
//             const newIndex = currentStepIndex + 1;
//             let status = 'In Progress';
//             if (newIndex === hiringSteps.length - 2) status = 'Offer Extended';
//             else if (newIndex === hiringSteps.length - 1) status = 'Hired';
//             await axios.put(`${BASE_URL}/api/recruitment/applications/${applicationId}/status`, 
//                 { currentStep: newIndex, status },
//                 { headers: { Authorization: `Bearer ${token}` } }
//             );
//             Swal.fire({ title: 'Success!', icon: 'success', timer: 1500, showConfirmButton: false });
//             fetchApplications();
//         } catch (error) {
//             Swal.fire('Error', 'Failed to advance step.', 'error');
//         }
//     };

//     const rejectCandidate = async (applicationId, app, e) => {
//         if (e) e.stopPropagation();
//         const result = await Swal.fire({
//             title: 'Reject Candidate?',
//             text: `REJECT ${app.name}?`,
//             icon: 'warning',
//             showCancelButton: true,
//             confirmButtonColor: '#ef4444',
//             confirmButtonText: 'Yes, reject'
//         });
//         if (!result.isConfirmed) return;
//         try {
//             const token = localStorage.getItem('token');
//             const currentStepName = hiringSteps[app.currentStep]?.stepName || 'Unknown Stage';
            
//             await axios.put(`${BASE_URL}/api/recruitment/applications/${applicationId}/status`,
//                 { 
//                     status: 'Rejected', 
//                     currentStep: app.currentStep, // Keep them at their current step
//                     stepName: currentStepName,
//                     rejectedAtStage: currentStepName
//                 },
//                 { headers: { Authorization: `Bearer ${token}` } }
//             );
//             Swal.fire({ title: 'Rejected', icon: 'info', timer: 1500, showConfirmButton: false });
//             fetchApplications();
//         } catch (error) {
//             Swal.fire('Error', 'Failed to update status.', 'error');
//         }
//     };

//     const getInitials = (name) => {
//         if (!name) return '??';
//         const parts = name.trim().split(/\s+/);
//         return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
//     };

//     const deleteApplication = async (applicationId, name, e) => {
//         if (e) e.stopPropagation();
//         const result = await Swal.fire({
//             title: 'Delete?',
//             text: `Permanently delete ${name}?`,
//             icon: 'warning',
//             showCancelButton: true,
//             confirmButtonColor: '#ef4444',
//             confirmButtonText: 'Yes, delete'
//         });
//         if (!result.isConfirmed) return;
//         try {
//             const token = localStorage.getItem('token');
//             await axios.delete(`${BASE_URL}/api/recruitment/applications/${applicationId}`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             Swal.fire('Deleted!', 'Removed successfully.', 'success');
//             fetchApplications();
//         } catch (error) {
//             Swal.fire('Error', 'Failed to delete.', 'error');
//         }
//     };

//     const generateHiringSteps = (app) => {
//         if (!hiringSteps || hiringSteps.length === 0) return null;
//         return (
//             <div className="rd-pipeline-steps-container">
//                 {hiringSteps.map((step, index) => {
//                     const isRejectedState = app.status === 'Rejected';
//                     const isCompleted = index < app.currentStep;
//                     const isCurrent = index === app.currentStep;
                    
//                     // Specific rejection logic
//                     const isRejectedPoint = isRejectedState && (app.rejectedAtStage === step.stepName || (index === app.currentStep && !app.rejectedAtStage));
//                     const isHired = app.status === 'Hired' && index === hiringSteps.length - 1;
                    
//                     let cls = 'rd-step-pending';
//                     if (isRejectedPoint) cls = 'rd-step-rejected';
//                     else if (isHired || (isCompleted && !isRejectedState)) cls = 'rd-step-completed';
//                     else if (isCurrent && !isRejectedState) cls = 'rd-step-current';
//                     else if (index > app.currentStep && isRejectedState) cls = 'rd-step-pending'; // Steps after rejection remain pending

//                     const canAdv = isCurrent && !isRejectedState && !isHired && index < hiringSteps.length - 1;
//                     return (
//                         <div key={index} className={`rd-pipeline-step ${cls} ${canAdv ? 'rd-step-advanceable' : ''}`}
//                             onClick={(e) => canAdv && advanceStep(app.id, index, e)}>
//                             <div className="rd-step-number">{isHired || (isCompleted && !isRejectedState) ? '✓' : isRejectedPoint ? '✗' : index + 1}</div>
//                             <div className="rd-step-label">{step.stepName}</div>
//                             {isCurrent && !isRejectedState && !isHired && (
//                                 <button className="rd-reject-step-btn" onClick={(e) => rejectCandidate(app.id, app, e)}><XCircle size={14} /></button>
//                             )}
//                         </div>
//                     );
//                 })}
//             </div>
//         );
//     };

//     const renderExpandedDetails = (app) => {
//         const submittedDate = formatDateTime(app.appliedAt || app.createdAt || app.created_at || app.submittedAt || app.submitted_at);
//         return (
//             <div className="candidate-expanded-content">
//                 <div className="submission-audit-row">
//                     <span><strong>Submitted:</strong> {submittedDate}</span>
//                     <span className="submitted-by"><strong>Submitted by:</strong> {app.submittedBy || 'admin'}</span>
//                 </div>

//                 <div className="detail-grid-section">
//                     <h4>Personal Information</h4>
//                     <div className="expanded-detail-grid">
//                         <div className="detail-field"><label>First Name</label><span>{app.candidateFirstName || 'N/A'}</span></div>
//                         <div className="detail-field"><label>Last Name</label><span>{app.candidateLastName || 'N/A'}</span></div>
//                         <div className="detail-field"><label>Email</label><span>{app.email}</span></div>
//                         <div className="detail-field"><label>Phone</label><span>{app.phone}</span></div>
//                         <div className="detail-field"><label>Date of Birth</label><span>{app.dateOfBirth ? new Date(app.dateOfBirth).toLocaleDateString() : 'N/A'}</span></div>
//                         <div className="detail-field"><label>Current Location</label><span>{app.currentLocation || 'N/A'}</span></div>
//                         <div className="detail-field"><label>LinkedIn</label>{app.linkedInUrl ? <a href={app.linkedInUrl} target="_blank" rel="noopener noreferrer">Link</a> : 'N/A'}</div>
//                         <div className="detail-field"><label>Passport</label><span>{app.passport || 'N/A'}</span></div>
//                         <div className="detail-field"><label>Work Auth</label><span>{app.workAuthorization || 'N/A'}</span></div>
//                         <div className="detail-field"><label>Date Availability</label><span>{app.dateAvailability ? new Date(app.dateAvailability).toLocaleDateString() : 'N/A'}</span></div>
//                     </div>
//                 </div>

//                 <div className="detail-grid-section">
//                 <h4>Professional Details</h4>
//                 <div className="expanded-detail-grid">
//                     <div className="detail-field"><label>Total IT Experience</label><span>{app.totalITExperience || app.experience || 'N/A'} {app.totalITExperience || app.experience ? 'Years' : ''}</span></div>
//                     <div className="detail-field"><label>Relevant Experience</label><span>{app.relevantExperience || 'N/A'} {app.relevantExperience ? 'Years' : ''}</span></div>
//                     <div className="detail-field"><label>Current Company</label><span>{app.currentCompany || 'N/A'}</span></div>
//                     <div className="detail-field"><label>Current Employer</label><span>{app.currentEmployer || 'N/A'}</span></div>
//                     <div className="detail-field"><label>Expected Salary</label><span>{app.expectedSalary || 'N/A'}</span></div>
//                     <div className="detail-field"><label>Rate</label><span>{app.rate || 'N/A'}</span></div>
//                     <div className="detail-field"><label>Notice Period</label><span>{app.noticePeriod} Days</span></div>
//                     <div className="detail-field"><label>Highest Degree</label><span>{app.highestDegree || 'N/A'}</span></div>
//                     <div className="detail-field full-width"><label>Skills</label><span className="skills-tags">{app.skills}</span></div>
//                     <div className="detail-field full-width"><label>Current Employer Address</label><span>{app.currentEmployerAddress || 'N/A'}</span></div>
//                 </div>
//             </div>

//             {(app.isFormerTCSEmployee || app.isFormerTCSBusinessAssociate) && (
//                 <div className="detail-grid-section">
//                     <h4>TCS Information</h4>
//                     <div className="expanded-detail-grid">
//                         <div className="detail-field"><label>Former TCS Employee</label><span>{app.isFormerTCSEmployee ? 'Yes' : 'No'}</span></div>
//                         {app.isFormerTCSEmployee && <div className="detail-field"><label>TCS Employee ID</label><span>{app.tcsEmployeeId || 'N/A'}</span></div>}
//                         <div className="detail-field"><label>Former TCS Business Associate</label><span>{app.isFormerTCSBusinessAssociate ? 'Yes' : 'No'}</span></div>
//                         {app.isFormerTCSBusinessAssociate && <div className="detail-field"><label>TCS Business Associate ID</label><span>{app.tcsBusinessAssociateId || 'N/A'}</span></div>}
//                     </div>
//                 </div>
//             )}

//             <div className="expanded-actions-row">
//                 <div className="resumes-list">
//                     <label>Resumes:</label>
//                     {applicationResumes[app.id]?.length > 0 ? (
//                         <div className="resume-chips">
//                             {applicationResumes[app.id].map(res => (
//                                 <button key={res.id} className="resume-link" onClick={(e) => viewResume(res.id, app.id, e)}>
//                                     <FileText size={12} /> {res.resumeFileName}
//                                 </button>
//                             ))}
//                         </div>
//                     ) : <span className="no-data">None</span>}
//                 </div>
//             </div>
//         </div>
//     );
//     };   

//     if (isLoading) return <div className="role-detail-loading"><div className="spinner"></div><p>Loading...</p></div>;
//     if (!role) return <div className="role-detail-error">Role not found.</div>;

//     const hiringStepCounts = calculateHiringStepCounts(applications);

//     return (
//         <div className="role-detail-page-container">
//             <div className="role-detail-top-nav">
//                 <button className="back-link-btn" onClick={() => navigate(-1)}><ChevronLeft size={18} /> Back</button>
//             </div>

//             <div className="role-detail-main-header">
//                 <div className="title-section">
//                     <h1>{role.role}</h1>
//                     <div className="badge-group">
//                         <span className={`status-badge ${role.status?.toLowerCase()}`}>{role.status}</span>
//                         <span className={`urgency-badge ${role.urgency?.toLowerCase()}`}>{role.urgency}</span>
//                     </div>
//                 </div>
//             </div>

//             <div className="rd-bento-grid">
//                 <div className="rd-bento-item rd-summary-box">
//                     <div className="card-header">
//                         <h2>Role Summary</h2>
//                         <div style={{ display: 'flex', gap: '4px' }}>
//                             <button className="source-candidate-btn" onClick={() => setShowSourceCandidateModal(true)}>Source Candidate</button>
//                             <button className="recruitment-submit-resume-btn" onClick={() => setShowResumeSubmissionModal(true)}>Submit Resume</button>
//                         </div>
//                     </div>
//                     <div className="hiring-progress-summary">
//                         <div className="summary-title"><h3>Pipeline</h3><span>Total: {applications.length}</span></div>
//                         <div className="stats-grid">
//                             {Object.entries(hiringStepCounts).map(([name, count]) => (
//                                 <div key={name} className="stat-box"><div className="stat-count">{count}</div><div className="stat-name">{name}</div></div>
//                             ))}
//                         </div>
//                     </div>
//                     <div className="details-section">
//                         <div className="detail-item">
//                             <div className="detail-label"><Hash size={14} /><label>Job ID</label></div>
//                             <span>{role.jobId || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Layers size={14} /><label>GBAMS ID</label></div>
//                             <span>{role.gbamsId || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Activity size={14} /><label>System ID</label></div>
//                             <span>JOB-{String(role.id).padStart(5, '0')}</span>
//                         </div>
//                         <div className="detail-item" data-tooltip={role.role}>
//                             <div className="detail-label"><Briefcase size={14} /><label>Role Title</label></div>
//                             <span>{role.role || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Activity size={14} /><label>Role Type</label></div>
//                             <span>{role.roleType || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Calendar size={14} /><label>Created</label></div>
//                             <span>{formatDateTime(role.created_at)}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Building size={14} /><label>Client</label></div>
//                             <span>{role.client || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><User size={14} /><label>Client POC</label></div>
//                             <span>{role.clientPocName || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item" data-tooltip={formatLocation(role)}>
//                             <div className="detail-label"><MapPin size={14} /><label>Location</label></div>
//                             <span>{formatLocation(role)}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Globe size={14} /><label>Work Mode</label></div>
//                             <span>{role.roleLocation || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Award size={14} /><label>Experience</label></div>
//                             <span>{role.experience || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><DollarSign size={14} /><label>Rate Range</label></div>
//                             <span>{role.rateRangeMin && role.rateRangeMax ? `${formatCurrency(role.rateRangeMin, role.currency)} - ${formatCurrency(role.rateRangeMax, role.currency)}` : 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Users size={14} /><label>Vendor</label></div>
//                             <span>{role.vendorName || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Globe size={14} /><label>Visa Types</label></div>
//                             <span>{role.visaTypes || 'N/A'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Clock size={14} /><label>Urgency</label></div>
//                             <span className={`urgency-text ${role.urgency?.toLowerCase()}`}>{role.urgency || 'Normal'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Activity size={14} /><label>Status</label></div>
//                             <span className={`status-text ${role.status?.toLowerCase()}`}>{role.status || 'Active'}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Calendar size={14} /><label>Start Date</label></div>
//                             <span>{formatDateTime(role.startDate)}</span>
//                         </div>
//                         <div className="detail-item">
//                             <div className="detail-label"><Calendar size={14} /><label>End Date</label></div>
//                             <span>{formatDateTime(role.endDate)}</span>
//                         </div>
//                     </div>
//                 </div>

//                 <div className="rd-bento-item rd-description-box">
//                     <div className="card-header"><h2>Job Description</h2></div>
//                     <div className="description-content-full" dangerouslySetInnerHTML={{ __html: role.jobDescription || 'No description.' }} />
//                 </div>

//                 <div className="rd-bento-item rd-candidates-box">
//                     <div className="section-header"><h2>Applications ({applications.length})</h2></div>
//                     {applications.length === 0 ? (
//                         <div className="no-candidates-state"><h3>No Applications</h3></div>
//                     ) : (
//                         <div className="candidates-list">
//                             {applications.map(app => (
//                                 <div key={app.id} className="candidate-card">
//                                     <div className="candidate-header">
//                                         <div className="candidate-info-wrapper">
//                                             <div className="candidate-avatar">{getInitials(app.name)}</div>
//                                             <div className="candidate-info"><h3>{app.name}</h3><p>{app.email} | {app.phone}</p></div>
//                                         </div>
//                                         <div className="candidate-actions">
//                                             <button className="edit-btn" onClick={(e) => openEditApplicationModal(app, e)}>Edit</button>
//                                             <button className="delete-btn" onClick={(e) => deleteApplication(app.id, app.name, e)}><Trash2 size={16} /></button>
//                                             <button className="expand-btn" onClick={(e) => toggleCandidateExpand(app.id, e)}>{expandedCandidateId === app.id ? 'Less ▲' : 'More ▼'}</button>
//                                         </div>
//                                     </div>
//                                     <div className="hiring-pipeline-view">
//                                         <div className="pipeline-header" onClick={(e) => toggleHiringProcess(app.id, e)}>
//                                             <span><strong>Status:</strong> {app.status === 'Rejected' ? `Rejected – ${app.rejectedAtStage || 'Unknown Stage'}` : app.status}</span>
//                                             <span>{showHiringProcess[app.id] ? '▲' : '▼'}</span>
//                                         </div>
//                                         {showHiringProcess[app.id] && <div className="pipeline-steps">{generateHiringSteps(app)}</div>}
//                                     </div>
//                                     {expandedCandidateId === app.id && renderExpandedDetails(app)}
//                                 </div>
//                             ))}
//                         </div>
//                     )}
//                 </div>
//             </div>

//             {showResumeModal && (
//                 <div className="recruitment-modal" onClick={() => setShowResumeModal(false)}>
//                     <div className="recruitment-modal-content" style={{ maxWidth: '1000px', height: '90vh' }} onClick={e => e.stopPropagation()}>
//                         <div className="recruitment-modal-header"><h2>Resume: {currentCandidateName}</h2><span className="recruitment-close" onClick={() => setShowResumeModal(false)}>&times;</span></div>
//                         <div className="recruitment-modal-body" style={{ height: 'calc(100% - 80px)', padding: 0 }}><iframe src={currentResumeUrl} width="100%" height="100%" title="Resume" /></div>
//                     </div>
//                 </div>
//             )}

//             <ResumeSubmissionModal selectedRole={role} showResumeSubmissionModal={showResumeSubmissionModal} closeResumeSubmissionModal={() => setShowResumeSubmissionModal(false)} fetchApplications={fetchApplications} showNotification={() => {}} refreshRolesData={() => {}} />
//             <EditApplicationModal showEditApplicationModal={showEditApplicationModal} applicationToEdit={applicationToEdit} closeEditApplicationModal={closeEditApplicationModal} fetchApplications={fetchApplications} selectedRole={role} showNotification={() => {}} applicationResumes={applicationResumes} fetchApplicationResumes={fetchApplicationResumes} viewResume={viewResume} />
//             <SourceCandidateModal isOpen={showSourceCandidateModal} onClose={() => setShowSourceCandidateModal(false)} role={role} onProfileSubmitted={fetchApplications} />
//         </div>
//     );
// };

// export default RoleDetailView;
